import { appAlert } from '@/lib/appAlert';
import { formatAmountForDisplay, formatGroupedAmount, parseGroupedAmount } from '@/lib/amountFormat';
﻿import { markNotificationsReadForEntity } from '@/lib/notifications';
import { getErrorMessage } from '@/lib/functions';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import * as Linking from 'expo-linking';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  AppState,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  Share,
  StyleSheet,
  Text,
  TextInput,
  View,
  type LayoutChangeEvent,
  type ScrollView as ScrollViewType,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { IconActionButton, IconActionRow } from '@/components/ui/IconActionButton';
import { LevelBadge, type LevelInfo } from '@/components/ui/LevelBadge';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { VerifiedBadge } from '@/components/ui/VerifiedBadge';
import { SignatureView } from '@/components/ui/SignatureView';
import { SensitiveContent } from '@/components/WebWatermark';
import { AdvancePaymentSection } from '@/components/AdvancePaymentSection';
import { ChatBubble, getAvailableApps } from '@/components/ChatBubble';
import { CurrencyHint } from '@/components/CurrencyHint';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SuccessOverlay } from '@/components/SuccessOverlay';
import { AgreementSealAnimation } from '@/components/AgreementSealAnimation';
import { MoneyHandoffAnimation } from '@/components/MoneyHandoffAnimation';
import { AgreementTearAnimation } from '@/components/AgreementTearAnimation';
import { HandshakeAnimation } from '@/components/HandshakeAnimation';
import { DisputeStatusBanner } from '@/components/DisputeStatusBanner';
import { RenewalBanner } from '@/components/RenewalBanner';
import { EvidenceAttachSection, OpenDisputeFlow, RespondDisputeFlow, ResolveDisputeFlow, RespondMutualResolutionFlow } from '@/components/DisputeFlow';
import { colors, fontSize, radius, shadow, spacing } from '@/constants/theme';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import type { Agreement, Payment, Profile, Review } from '@/lib/database.types';
import {
  agreementIsFree,
  agreeToCancellation,
  checkPairAgreementLimit,
  confirmCompletion,
  rejectCompletion,
  confirmPayment,
  rejectPayment,
  rejectCancellation,
  declarePayment,
  checkCompletionAllowed,
  fetchProfilesByIds,
  getAgreement,
  listPayments,
  myFreeRemaining,
  requestCompletion,
  requestCancellation,
  requestAgreementEdit,
  respondAgreementEditRequest,
  saveAgreementEdit,
  finalizeAgreementEdit,
  sendAgreement as sendAgreementRpc,
  updateAgreementStatus,
  withdrawToDraft,
} from '@/lib/agreements';
import {
  fetchAgreementParties,
  signMultiPartyAgreement,
  openMultiPartyDispute,
  respondMultiPartyDispute,
  castDisputeVote,
  fetchMultiPartyDispute,
  fetchDisputeVotes,
  fetchDisputeStatements,
  requestMultiPartyCancellation,
  respondMultiPartyCancellation,
  withdrawFromMultiPartyAgreement,
  type AgreementParty,
  type MultiPartyDispute,
} from '@/lib/multiParty';
import { disputeTagLabel, DISPUTE_TAGS } from '@/lib/disputes';
import {
  commitmentIntro,
  counterpartyDisplayName,
  formatAgreementAmount,
  formatAmount,
  formatDate,
  formatDateTime,
  genderedArticle,
  paymentStatusColor,
  paymentStatusLabel,
  signatureCode,
  statusLabel,

} from '@/lib/format';
import { startAgreementAcceptance } from '@/lib/payments';
import { playSign } from '@/lib/sounds';
import { exportAgreementPdf } from '@/lib/agreementPdf';
import { getBusinessIdentityFields } from '@/lib/businesses';
import { acceptedAdvanceTotal, listAdvancePayments } from '@/lib/advancePayments';

import { getUserLevel } from '@/lib/levels';
import { fetchMyReliabilityPercent, getMyReviewForAgreement, getMyReviewsForAgreement } from '@/lib/reviews';
import { parseSignature } from '@/lib/signature';
import { recordSigningEvent } from '@/lib/signingEvents';
import {
  cancelTagLabel,
  CANCEL_TAGS_REQUESTER,
  CANCEL_TAGS_AGREE,
} from '@/lib/cancellation';
import { getDisputeForAgreement, type DisputeInfo } from '@/lib/disputes';
import { supabase } from '@/lib/supabase';
import { StarRating } from '@/components/ui/StarRating';
import { ErrorText } from '@/components/ErrorText';

const REVIEWABLE_STATUSES = ['completed'] as const;

type PendingAction =
  | 'accept'
  | 'reject'
  | 'withdraw'
  | 'complete'
  | 'terminate'
  | 'cancel_request'
  | 'cancel_agree'
  | 'request_completion'
  | 'confirm_completion'
  | 'reject_completion'
  | 'declare_payment'
  | 'confirm_payment'
  | 'reject_payment'
  | 'reject_cancel'
  | 'multi_cancel_request';

const TAG_ACTIONS: PendingAction[] = ['cancel_request', 'cancel_agree'];
const RELIABILITY_ACTIONS: PendingAction[] = ['complete', 'cancel_agree', 'confirm_completion', 'terminate'];
const SUPABASE_URL = process.env.EXPO_PUBLIC_SUPABASE_URL ?? '';

type DisputeFlowKind = 'open' | 'respond' | 'resolve' | null;

/** Όνομα συμβαλλόμενου πολλών ατόμων — fallback στο email πρόσκλησης όσο δεν έχει εγγραφεί. */
function partyDisplayName(mp: AgreementParty, profiles: Record<string, Profile>, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (mp.user_id) return profiles[mp.user_id]?.full_name?.trim() || t('common.user');
  return mp.party_email ? t('agreementDetail.inviteeLabel', { email: mp.party_email }) : t('common.user');
}

/** Snapshot των πεδίων που εμφανίζονται στο εξαγόμενο PDF — αλλαγή του σημαίνει ότι το PDF είναι πλέον ξεπερασμένο. */
function pdfSnapshot(agreement: Agreement, payments: Payment[]): string {
  return JSON.stringify({
    status: agreement.status,
    amount: agreement.amount,
    title: agreement.title,
    description: agreement.description,
    updated_at: (agreement as Agreement & { updated_at?: string }).updated_at,
    payments: payments.map((p) => `${p.id}:${p.status}:${p.captured_at ?? ''}`),
  });
}

/** Ετικέτα/χρώμα χρέωσης βάσει του σταδίου του hold. */
function holdLabel(p: Payment, t: (key: string) => string): { text: string; color: string } {
  if (p.captured_at) return { text: t('agreements.payment_status_charged'), color: colors.success };
  if (p.voided_at) return { text: t('agreements.payment_status_cancelled'), color: colors.textMuted };
  if (p.authorized_at) return { text: t('agreements.payment_status_authorized'), color: colors.warning };
  return { text: paymentStatusLabel(p.status), color: paymentStatusColor(p.status) };
}

export default function AgreementDetailScreen() {
  const { t } = useTranslation();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { user } = useAuth();
  const { profile: myProfile } = useProfile();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const scrollRef = useRef<ScrollViewType>(null);
  // Το σταθερό footer (κουμπιά ενεργειών) έχει το δικό του εσωτερικό scroll —
  // όταν υπάρχει ένα ActionBox που περιμένει απόφαση/αξιολόγηση, κάνουμε αυτόματο
  // scroll σε αυτό μόλις ανοίξει η οθόνη, ώστε ο χρήστης που ήρθε από
  // notification να το βλέπει αμέσως, χωρίς να ψάχνει.
  const footerScrollRef = useRef<ScrollViewType>(null);
  const pendingActionYRef = useRef<number | null>(null);
  const scrolledToActionRef = useRef(false);
  const advancePaymentYRef = useRef<number | null>(null);
  const advancePaymentPendingRef = useRef(false);
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const [partyLevels, setPartyLevels] = useState<Record<string, LevelInfo>>({});
  const [payments, setPayments] = useState<Payment[]>([]);
  const [myReview, setMyReview] = useState<Review | null>(null);
  const [myMultiReviews, setMyMultiReviews] = useState<Review[]>([]);
  const [isFree, setIsFree] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const [expandedActionKey, setExpandedActionKey] = useState<string | null>(null);
  // Pulse/glow στο κουμπί "Απάντηση στη Διαφωνία" — σηματοδοτεί ότι χρειάζεται
  // οπωσδήποτε δράση (δεν πρέπει να χαθεί μέσα σε μια μεγάλη σελίδα συμφωνίας).
  const respondPulse = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(respondPulse, { toValue: 1, duration: 700, useNativeDriver: false }),
        Animated.timing(respondPulse, { toValue: 0, duration: 700, useNativeDriver: false }),
      ]),
    );
    loop.start();
    return () => loop.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [modalError, setModalError] = useState<string | null>(null);
  const [earlyCompletionExpiry, setEarlyCompletionExpiry] = useState<string | null>(null);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
  const [showInsufficientInk, setShowInsufficientInk] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSeal, setShowSeal] = useState(false);
  const [showMoneyHandoff, setShowMoneyHandoff] = useState(false);
  const [showHandshake, setShowHandshake] = useState(false);
  const [showTear, setShowTear] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dispute, setDispute] = useState<DisputeInfo | null>(null);
  const [disputeFlow, setDisputeFlow] = useState<DisputeFlowKind>(null);
  // Αντί να ξαναφορτώνει αυτόματα (και να «τρεμοπαίζει»/παγώνει η οθόνη) σε
  // κάθε realtime event — π.χ. ακριβώς όταν ο ίδιος ο χρήστης μόλις κατέγραψε
  // προκαταβολή και το echo του realtime φτάνει λίγο αργότερα — δείχνουμε ένα
  // διακριτικό bubble και αφήνουμε τον χρήστη να αποφασίσει πότε να φορτώσει
  // τη νέα κατάσταση.
  const [hasUpdate, setHasUpdate] = useState(false);
  // Ένδειξη "Ενημερώθηκε" πάνω στο κουμπί PDF — δείχνει ότι κάτι άλλαξε στη
  // συμφωνία (ποσό/status/πληρωμές) μετά την τελευταία εξαγωγή PDF, ώστε ο
  // χρήστης να ξέρει ότι το PDF που κατέβασε παλιότερα δεν είναι πια πλήρες.
  const [pdfOutdated, setPdfOutdated] = useState(false);
  const lastExportSnapshotRef = useRef<string | null>(null);

  // ── Αίτημα Επεξεργασίας ──
  const [showEditRequestModal, setShowEditRequestModal] = useState(false);
  const [editRequestNote, setEditRequestNote] = useState('');
  const [showEditForm, setShowEditForm] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editBusy, setEditBusy] = useState(false);

  // ── Πολλαπλοί συμβαλλόμενοι (πρωτότυπο) ──
  const [multiParties, setMultiParties] = useState<AgreementParty[]>([]);
  const [multiDispute, setMultiDispute] = useState<MultiPartyDispute | null>(null);
  const [multiBusy, setMultiBusy] = useState(false);
  const [showAccuseModal, setShowAccuseModal] = useState(false);
  const [accuseStep, setAccuseStep] = useState<1 | 2 | 3>(1);
  const [accuseTarget, setAccuseTarget] = useState<string | null>(null);
  const [accuseTags, setAccuseTags] = useState<string[]>([]);
  const [accuseNote, setAccuseNote] = useState('');
  const [accuseEvidence, setAccuseEvidence] = useState<string[]>([]);
  const [showDisagreeModal, setShowDisagreeModal] = useState(false);
  const [disagreeStep, setDisagreeStep] = useState<1 | 2 | 3>(1);
  const [disagreeTags, setDisagreeTags] = useState<string[]>([]);
  const [disagreeNote, setDisagreeNote] = useState('');
  const [disagreeEvidence, setDisagreeEvidence] = useState<string[]>([]);
  const [disputeStatements, setDisputeStatements] = useState<{ user_id: string; tags: string[]; statement: string | null }[]>([]);
  const [disputeVotes, setDisputeVotes] = useState<{ voter_id: string; vote_for: string | null }[]>([]);

  async function sendCompletionRequest() {
    try {
      setBusy(true);
      await requestCompletion(agreement!.id);
      setSuccess(t('agreements.completion_requested'));
    } catch (e) {
      setError(getErrorMessage(e, t('agreementDetail.sendFailed')));
    } finally {
      setBusy(false);
    }
  }

  const lastLoadFinishedAtRef = useRef(0);
  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const agr = await getAgreement(id);
      setAgreement(agr);
      if (agr) {
        const mpPartiesResult = await fetchAgreementParties(agr.id).catch(() => null);
        if (mpPartiesResult) setMultiParties(mpPartiesResult);
        const mpParties = mpPartiesResult ?? [];
        const mpIds = mpParties.map((p) => p.user_id).filter((uid): uid is string => !!uid);
        setProfiles(await fetchProfilesByIds([agr.creator_id, agr.counterparty_id ?? '', ...mpIds]));
        if (mpParties.length > 0) {
          const mpDispute = await fetchMultiPartyDispute(agr.id).catch(() => null);
          setMultiDispute(mpDispute);
          if (mpDispute && mpDispute.status === 'voting') {
            setDisputeStatements(await fetchDisputeStatements(mpDispute.id).catch(() => []));
            setDisputeVotes(await fetchDisputeVotes(mpDispute.id).catch(() => []));
          } else {
            setDisputeStatements([]);
            setDisputeVotes([]);
          }
        } else {
          setMultiDispute(null);
          setDisputeStatements([]);
          setDisputeVotes([]);
        }
        const ids = [agr.creator_id, agr.counterparty_id].filter(Boolean) as string[];
        const lvls = await Promise.all(ids.map((uid) => getUserLevel(uid).catch(() => null)));
        const lvlMap: Record<string, LevelInfo> = {};
        ids.forEach((uid, i) => { if (lvls[i]) lvlMap[uid] = lvls[i] as LevelInfo; });
        setPartyLevels(lvlMap);
        setPayments(await listPayments(agr.id));
        // Φόρτωση dispute αν η συμφωνία είναι disputed ή υπάρχει ανοιχτή διαφωνία
        let fetchedDispute: typeof dispute = null;
        try {
          fetchedDispute = await getDisputeForAgreement(agr.id);
          setDispute(fetchedDispute);
        } catch {
          setDispute(null);
        }
        // Το myReview πρέπει να φορτώνεται και μετά από επιλυμένη διαφωνία
        // (η συμφωνία καταλήγει σε 'cancelled', όχι 'completed') — αλλιώς η
        // ένδειξη "ήδη αξιολόγησες" δεν ενημερώνεται ποτέ και το ActionBox
        // παραμένει εμφανές για πάντα ακόμα κι αν η αξιολόγηση υποβλήθηκε.
        const fetchedDisputeConcluded = fetchedDispute?.status === 'resolved' || fetchedDispute?.status === 'auto_cancelled';
        // getMyReviewForAgreement() (single row) είναι μόνο για 2-ατόμων συμφωνίες —
        // σε multi-party ένας reviewer έχει ΠΟΛΛΕΣ εγγραφές (μία ανά reviewee),
        // οπότε η singular εκδοχή θα έσκαγε "multiple rows returned".
        if (((REVIEWABLE_STATUSES as readonly string[]).includes(agr.status) || fetchedDisputeConcluded) && user && mpParties.length === 0) {
          setMyReview(await getMyReviewForAgreement(agr.id, user.id));
        }
        if (user) {
          setMyMultiReviews(await getMyReviewsForAgreement(agr.id, user.id).catch(() => []));
        }
        // Δωρεάν ή 1€; (για το κουμπί αποδοχής στο pending)
        const mine = agr.creator_id === user?.id ? agr.creator_agreed : agr.counterparty_agreed;
        if (agr.status === 'pending' && !mine) {
          try {
            if (agr.from_offer && agr.creator_id === user?.id) {
              // Offer agreement: μόνο ο creator πληρώνει.
              // Ελέγχουμε μόνο τα δικά του free credits (my_free_remaining)
              // και όχι αμφότερα τα μέρη (agreementIsFree).
              const remaining = await myFreeRemaining();
              setIsFree(remaining > 0);
            } else {
              setIsFree(await agreementIsFree(agr.id));
            }
          } catch {
            setIsFree(null);
          }
        }
      }
    } catch (e) {
      setError(getErrorMessage(e, t('common.loadFailed')));
    } finally {
      setLoading(false);
      setHasUpdate(false);
      lastLoadFinishedAtRef.current = Date.now();
    }
    // Σκόπιμα εξαρτάται από το σταθερό user?.id, όχι το ίδιο το user object —
    // το AuthContext δημιουργεί νέο user object σε κάθε token refresh
    // (onAuthStateChange), κάτι που ξανάφτιαχνε αυτό το callback, ξανάτρεχε
    // το useFocusEffect παρακάτω, ξαναέφτιαχνε το realtime channel και
    // ξανακαλούσε load() — ορατό τρεμόπαιγμα εντελώς ασύνδετο με πραγματικές
    // αλλαγές στη συμφωνία.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id]);

  // Εγγυημένο πρώτο φόρτωμα, ανεξάρτητο από το "focus" event — σε cold start
  // από deep link (π.χ. tap σε push notification) το useFocusEffect παρακάτω
  // δεν πυροδοτείται πάντα αξιόπιστα την πρώτη φορά, πριν είναι πλήρως
  // έτοιμος ο navigator, αφήνοντας την οθόνη κολλημένη σε loading.
  useEffect(() => {
    void load();
  }, [load]);

  // Αν έχει ήδη γίνει εξαγωγή PDF και κάτι σχετικό άλλαξε από τότε, δείχνουμε
  // "Ενημερώθηκε" πάνω στο κουμπί ώστε ο χρήστης να ξέρει να το ξανακατεβάσει.
  useEffect(() => {
    if (!agreement || lastExportSnapshotRef.current === null) return;
    setPdfOutdated(pdfSnapshot(agreement, payments) !== lastExportSnapshotRef.current);
  }, [agreement, payments]);

  useFocusEffect(
    useCallback(() => {
      void load();
      if (!id) return;
      void markNotificationsReadForEntity(id);
      // Δεν ξαναφορτώνουμε πια αυτόματα ΟΛΗ την οθόνη σε κάθε realtime event —
      // αυτό προκαλούσε ορατό «τρεμόπαιγμα»/πάγωμα, ειδικά σε ενέργειες όπως η
      // καταγραφή προκαταβολής όπου το echo της ΔΙΚΗΣ ΤΟΥ αλλαγής έφτανε λίγο
      // αργότερα σε αυτόν που μόλις την έκανε. Αντ' αυτού μαζεύουμε τα events
      // (debounce) και απλά δείχνουμε ένα bubble "Νέα κατάσταση" — ο χρήστης
      // πατάει για να φορτώσει όποτε θέλει.
      let debounceTimer: ReturnType<typeof setTimeout> | null = null;
      function debouncedLoad() {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          setHasUpdate(true);
        }, 600);
      }
      const channel = supabase
        .channel(`agreement-${id}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'agreements',
          filter: `id=eq.${id}`,
        }, debouncedLoad)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'advance_payments',
          filter: `agreement_id=eq.${id}`,
        }, debouncedLoad)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'disputes',
          filter: `agreement_id=eq.${id}`,
        }, debouncedLoad)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'agreement_parties',
          filter: `agreement_id=eq.${id}`,
        }, debouncedLoad)
        .subscribe();

      const appStateSub = AppState.addEventListener('change', (state) => {
        if (state === 'active') void load();
      });

      return () => {
        if (debounceTimer) clearTimeout(debounceTimer);
        void supabase.removeChannel(channel);
        appStateSub.remove();
      };
    }, [load, id]),
  );

  async function onRefresh() {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }

  /** Παραγωγή & κοινοποίηση PDF πιστοποιητικού της συμφωνίας. */
  async function onExportPdf() {
    if (!agreement || exporting) return;
    setExporting(true);
    setError(null);
    try {
      let advance;
      if (agreement.amount != null && agreement.amount_unit !== 'percent') {
        const items = await listAdvancePayments(agreement.id);
        const accepted = items.filter((i) => i.status === 'accepted');
        const acceptedTotal = acceptedAdvanceTotal(items);
        if (acceptedTotal > 0) {
          advance = {
            acceptedTotal,
            remaining: Math.max(0, Number(agreement.amount) - acceptedTotal),
            items: accepted.map((i) => ({
              amount: Number(i.amount),
              note: i.note,
              date: i.created_at,
            })),
          };
        }
      }
      // Αν η προσφορά που έγινε αυτή η συμφωνία στάλθηκε "ως η επιχείρηση", το
      // PDF δείχνει τα επιχειρηματικά στοιχεία (λογότυπο/όροι) του δημιουργού
      // της επιχείρησης αντί για το προσωπικό προφίλ του δημιουργού της συμφωνίας.
      let profilesForPdf = profiles;
      const senderBusinessId = (agreement as unknown as { sender_business_id?: string | null }).sender_business_id;
      if (senderBusinessId) {
        const bizFields = await getBusinessIdentityFields(senderBusinessId);
        if (bizFields) {
          profilesForPdf = {
            ...profiles,
            [agreement.creator_id]: { ...profiles[agreement.creator_id], ...bizFields },
          };
        }
      }
      await exportAgreementPdf(agreement, profilesForPdf, advance, multiParties);
      lastExportSnapshotRef.current = pdfSnapshot(agreement, payments);
      setPdfOutdated(false);
    } catch (e) {
      setError(getErrorMessage(e, t('agreementDetail.pdfExportFailed')));
    } finally {
      setExporting(false);
    }
  }

  async function onSubmitEditRequest() {
    if (!agreement || editBusy) return;
    if (editRequestNote.trim().length < 5) {
      setError(t('agreementDetail.editRequestNoteRequired'));
      return;
    }
    setEditBusy(true);
    setError(null);
    try {
      await requestAgreementEdit(agreement.id, editRequestNote.trim());
      setShowEditRequestModal(false);
      setEditRequestNote('');
      setSuccess(t('agreementDetail.editRequestSent'));
      await load();
    } catch (e) {
      setError(getErrorMessage(e, t('agreementDetail.editRequestSendFailed')));
    } finally {
      setEditBusy(false);
    }
  }

  async function onRespondEditRequest(approve: boolean) {
    if (!agreement || editBusy) return;
    setEditBusy(true);
    setError(null);
    try {
      await respondAgreementEditRequest(agreement.id, approve);
      setSuccess(approve ? t('agreementDetail.editRequestApproved') : t('agreementDetail.editRequestRejected'));
      await load();
    } catch (e) {
      setError(getErrorMessage(e, t('agreementDetail.actionFailed')));
    } finally {
      setEditBusy(false);
    }
  }

  function openEditForm() {
    if (!agreement) return;
    setEditTitle(agreement.title);
    setEditDescription(agreement.description ?? '');
    setEditAmount(agreement.amount != null ? formatAmountForDisplay(agreement.amount) : '');
    setShowEditForm(true);
  }

  async function onSaveEditForm() {
    if (!agreement || editBusy) return;
    setEditBusy(true);
    setError(null);
    try {
      await saveAgreementEdit(agreement.id, {
        title: editTitle.trim(),
        description: editDescription.trim() || null,
        amount: editAmount.trim() ? parseGroupedAmount(editAmount) : null,
        currency: agreement.currency,
        amount_unit: (agreement as { amount_unit?: string | null }).amount_unit ?? null,
        start_date: (agreement as { start_date?: string | null }).start_date ?? null,
        end_date: (agreement as { end_date?: string | null }).end_date ?? null,
        expires_at: agreement.expires_at ?? null,
        expires_at_label: agreement.expires_at_label ?? null,
      });
      setShowEditForm(false);
      setSuccess(t('agreementDetail.editSavedPendingApproval'));
      await load();
    } catch (e) {
      setError(getErrorMessage(e, t('agreementDetail.saveFailed')));
    } finally {
      setEditBusy(false);
    }
  }

  async function onFinalizeEdit(approve: boolean) {
    if (!agreement || editBusy) return;
    setEditBusy(true);
    setError(null);
    try {
      await finalizeAgreementEdit(agreement.id, approve);
      setSuccess(approve ? t('agreementDetail.editFinalized') : t('agreementDetail.editFinalizeRejected'));
      await load();
    } catch (e) {
      setError(getErrorMessage(e, t('agreementDetail.actionFailed')));
    } finally {
      setEditBusy(false);
    }
  }

  async function onSignMultiParty() {
    if (!agreement || multiBusy) return;
    setMultiBusy(true);
    setError(null);
    try {
      await signMultiPartyAgreement(agreement.id);
      // Optimistic update — δεν βασιζόμαστε ΜΟΝΟ στο load() να επαναφορτώσει
      // σωστά, γιατί ένα στιγμιαίο πρόβλημα δικτύου στο reload άφηνε το κουμπί
      // "κολλημένο" (loading) μέχρι να ξαναμπεί ο χρήστης στη συμφωνία.
      setMultiParties((prev) => prev.map((mp) => (mp.user_id === user?.id ? { ...mp, has_signed: true } : mp)));
      playSign();
      setShowSeal(true);
      await load();
    } catch (e) {
      setError(getErrorMessage(e, t('agreementDetail.signFailed')));
    } finally {
      setMultiBusy(false);
    }
  }

  async function onOpenMultiPartyDispute() {
    if (!agreement || !accuseTarget || multiBusy) return;
    setMultiBusy(true);
    setError(null);
    try {
      await openMultiPartyDispute(agreement.id, accuseTarget, accuseTags, accuseNote, accuseEvidence);
      setShowAccuseModal(false);
      setAccuseTarget(null);
      setAccuseTags([]);
      setAccuseNote('');
      setAccuseEvidence([]);
      setSuccess(t('agreementDetail.disputeOpened'));
      await load();
    } catch (e) {
      setError(getErrorMessage(e, t('agreementDetail.disputeOpenFailed')));
    } finally {
      setMultiBusy(false);
    }
  }

  async function onRespondMultiParty(accept: boolean, tags?: string[], statement?: string) {
    if (!multiDispute || multiBusy) return;
    setMultiBusy(true);
    setError(null);
    try {
      await respondMultiPartyDispute(multiDispute.id, accept, tags ?? [], statement, disagreeEvidence);
      setShowDisagreeModal(false);
      setDisagreeTags([]);
      setDisagreeNote('');
      setDisagreeEvidence([]);
      setSuccess(accept ? t('agreementDetail.blameAccepted') : t('agreementDetail.disputeGoesToVote'));
      await load();
    } catch (e) {
      setError(getErrorMessage(e, t('agreementDetail.actionFailed')));
    } finally {
      setMultiBusy(false);
    }
  }

  async function onCastVote(voteFor: string | null) {
    if (!multiDispute || multiBusy) return;
    setMultiBusy(true);
    setError(null);
    try {
      await castDisputeVote(multiDispute.id, voteFor);
      setSuccess(t('agreementDetail.voteRecorded'));
      await load();
    } catch (e) {
      setError(getErrorMessage(e, t('agreementDetail.voteFailed')));
    } finally {
      setMultiBusy(false);
    }
  }

  function onRequestMultiCancel() {
    if (!agreement) return;
    setPendingAction('multi_cancel_request');
  }

  async function onRespondMultiCancel(agree: boolean) {
    if (!agreement || multiBusy) return;
    setMultiBusy(true);
    setError(null);
    try {
      const result = await respondMultiPartyCancellation(agreement.id, agree, []);
      if (result.rejected) setSuccess(t('agreementDetail.multiCancelRejectedContinues'));
      else if (result.cancelled) setSuccess(t('agreementDetail.multiCancelAllAgreedDone'));
      else setSuccess(t('agreementDetail.multiCancelVoteRecorded', { agreed: result.agreed, total: result.total }));
      await load();
    } catch (e) {
      setError(getErrorMessage(e, t('agreementDetail.actionFailed')));
    } finally {
      setMultiBusy(false);
    }
  }

  function onWithdrawMultiParty() {
    if (!agreement) return;
    const myRow = multiParties.find((mp) => mp.user_id === user?.id);
    if (!myRow || myRow.role === 'creator') return;
    setShowWithdrawConfirm(true);
  }

  async function doWithdrawMultiParty() {
    if (!agreement) return;
    setShowWithdrawConfirm(false);
    setMultiBusy(true);
    setError(null);
    try {
      await withdrawFromMultiPartyAgreement(agreement.id, []);
      setSuccess(t('agreementDetail.multiWithdrawDone'));
      await load();
    } catch (e) {
      setError(getErrorMessage(e, t('agreementDetail.actionFailed')));
    } finally {
      setMultiBusy(false);
    }
  }

  async function onShareLink() {
    if (!agreement) return;
    // Πάντα υπάρχει κωδικός στο link που μοιραζόμαστε — αν δεν έχει
    // δημιουργηθεί ήδη (π.χ. ο αντισυμβαλλόμενος προστέθηκε ήδη εγγεγραμμένος,
    // χωρίς email πρόσκλησης), το RPC δημιουργεί έναν τώρα.
    const { data: inviteCode } = await (supabase.rpc as any)('ensure_agreement_invite_code', { p_agreement_id: agreement.id }) as { data: string | null };
    // Ο κωδικός ΔΕΝ μπαίνει στο URL — αν το link σταλεί κατά λάθος σε λάθος
    // άτομο, δεν πρέπει να μπορεί να δει τη συμφωνία αυτόματα. Μένει μόνο
    // ως ξεχωριστό κείμενο στο μήνυμα (codeLine), που ο παραλήπτης αντιγράφει.
    const url = `https://heirapsia.gr/landing-page/agreement-invite.html?id=${agreement.id}`;
    const codeLine = inviteCode ? `\n${t('agreements.share_invite_code', { code: inviteCode })}` : '';
    await Share.share({
      title: agreement.title,
      message: `${t('agreements.share_message')} «${agreement.title}»:${codeLine}\n${url}`,
      url,
    });
  }

  /** Αποστολή (δωρεάν): draft→pending + email πρόσκλησης αν ο αντισυμβαλλόμενος δεν είναι εγγεγραμμένος. */
  async function sendAgreement() {
    if (!agreement || !user) return;
    setBusy(true);
    setError(null);
    try {
      if (agreement.counterparty_id) {
        const pairCheck = await checkPairAgreementLimit(user.id, agreement.counterparty_id);
        if (!pairCheck.allowed && pairCheck.available_at) {
          const formatted = new Date(pairCheck.available_at).toLocaleString('el-GR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
          setError(t('errors.cooldown24h', { time: formatted }));
          return;
        }
      }
      const updated = await sendAgreementRpc(agreement.id);
      setAgreement(updated);

      if (!updated.counterparty_id && updated.counterparty_email) {
        setSuccess(t('agreements.sent_invite_body', { email: updated.counterparty_email }));
      } else {
        setSuccess(t('agreements.sent_body'));
      }
      await load();
    } catch (e) {
      setError(getErrorMessage(e, t('agreementDetail.sendActionFailed')));
    } finally {
      setBusy(false);
    }
  }

  async function doAccept() {
    if (!agreement || !user) return;

    const verificationMethod: 'none' | 'phone' | 'id' =
      myProfile?.verified ? 'id' : myProfile?.phone_verified ? 'phone' : 'none';

    // Πάντα μέσω edge function — αυτή χειρίζεται δωρεάν/μελάνι με service role.
    const res = await startAgreementAcceptance(agreement.id);
    if (res.kind === 'insufficientInk') {
      setShowInsufficientInk(true);
      return;
    }
    // Η ίδια η αποδοχή έχει ήδη ολοκληρωθεί server-side (startAgreementAcceptance) —
    // αν αποτύχει μόνο η καταγραφή IP/συσκευής/μεθόδου επαλήθευσης δεν πρέπει να
    // μπλοκάρει ή να μπερδέψει τον χρήστη σαν να απέτυχε η υπογραφή. Το await
    // εδώ εξασφαλίζει ότι τουλάχιστον ΚΑΤΑΓΡΑΦΕΤΑΙ το σφάλμα αντί να χαθεί
    // εντελώς σιωπηλά (πριν ήταν "void", χωρίς await ή catch).
    try {
      await recordSigningEvent(agreement.id, user.id, verificationMethod);
    } catch (e) {
      console.error('[doAccept] recordSigningEvent failed — η υπογραφή ολοκληρώθηκε αλλά το αποδεικτικό IP/συσκευής όχι', e);
    }
    playSign();
    await load();
    setShowSeal(true);
  }

  function closeConfirm() {
    setPendingAction(null);
    setSelectedTags([]);
    setModalError(null);
  }

  function toggleTag(key: string) {
    setSelectedTags((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }

  async function runConfirm() {
    const action = pendingAction;
    if (!action || !agreement || !user) return;
    if (TAG_ACTIONS.includes(action) && selectedTags.length === 0) {
      setModalError(t('errors.selectAtLeastOneTag'));
      return;
    }
    setBusy(true);
    setModalError(null);
    // Ολοκλήρωση/τερματισμός/αμοιβαία ακύρωση αλλάζουν το ποσοστό αξιοπιστίας
    // (μετράει completed+terminated έναντι cancelled_mutual/unilateral) — δείχνουμε
    // στον χρήστη το πριν/μετά ώστε να ξέρει πώς επηρέασε η ενέργειά του.
    const tracksReliability = RELIABILITY_ACTIONS.includes(action);
    const reliabilityBefore = tracksReliability
      ? await fetchMyReliabilityPercent(user.id, myProfile?.phone_verified, myProfile?.verified).catch(() => null)
      : null;
    try {
      switch (action) {
        case 'accept':
          await doAccept();
          break;
        case 'reject':
          setAgreement(await updateAgreementStatus(agreement.id, 'cancelled'));
          setShowTear(true);
          break;
        case 'withdraw':
          try {
            await withdrawToDraft(agreement.id);
            await load();
            setShowTear(true);
          } catch (err: unknown) {
            const msg = getErrorMessage(err);
            if (msg.includes('counterparty_already_signed')) {
              setError(t('errors.cannotWithdrawSigned'));
            } else {
              setError(msg);
            }
          }
          break;
        case 'complete':
          setAgreement(await updateAgreementStatus(agreement.id, 'completed'));
          setShowHandshake(true);
          break;
        case 'terminate':
          setAgreement(await updateAgreementStatus(agreement.id, 'terminated'));
          break;
        case 'cancel_request':
          setAgreement(await requestCancellation(agreement.id, user.id, selectedTags));
          break;
        case 'cancel_agree':
          setAgreement(await agreeToCancellation(agreement.id, selectedTags));
          setShowTear(true);
          break;
        case 'reject_cancel':
          setAgreement(await rejectCancellation(agreement.id));
          break;
        case 'request_completion': {
          const completionCheck = await checkCompletionAllowed(agreement.id);
          if (!completionCheck.allowed) {
            const canCompleteAtStr = completionCheck.can_complete_at
              ? formatDateTime(completionCheck.can_complete_at)
              : null;
            appAlert(
              t('agreements.too_early_title'),
              canCompleteAtStr
                ? t('agreements.too_early_body_withTime', { time: canCompleteAtStr })
                : t('agreements.too_early_body'),
            );
            break;
          }
          const isBeforeExpiry = agreement.expires_at && new Date(agreement.expires_at) > new Date();
          if (isBeforeExpiry) {
            const expiryStr = new Date(agreement.expires_at!).toLocaleDateString('el-GR', { day: 'numeric', month: 'long' });
            setEarlyCompletionExpiry(expiryStr);
            break;
          }
          await requestCompletion(agreement.id);
          setSuccess(t('agreements.completion_requested'));
          break;
        }
        case 'confirm_completion':
          await confirmCompletion(agreement.id);
          setShowHandshake(true);
          break;
        case 'reject_completion':
          await rejectCompletion(agreement.id);
          setSuccess(t('agreementDetail.completionRejectedContinues'));
          break;
        case 'declare_payment':
          await declarePayment(agreement.id);
          setShowMoneyHandoff(true);
          break;
        case 'confirm_payment':
          await confirmPayment(agreement.id);
          setShowMoneyHandoff(true);
          break;
        case 'reject_payment':
          await rejectPayment(agreement.id);
          setSuccess(t('agreements.payment_rejected'));
          break;
        case 'multi_cancel_request':
          await requestMultiPartyCancellation(agreement.id, []);
          setSuccess(t('agreementDetail.multiCancelRequestSent'));
          break;
      }
      setPendingAction(null);
      setSelectedTags([]);
      await load();
      if (tracksReliability && reliabilityBefore != null) {
        const reliabilityAfter = await fetchMyReliabilityPercent(user.id, myProfile?.phone_verified, myProfile?.verified).catch(() => null);
        if (reliabilityAfter != null && reliabilityAfter !== reliabilityBefore) {
          const diff = Math.round((reliabilityAfter - reliabilityBefore) * 10) / 10;
          setTimeout(() => {
            appAlert(
              t('agreementDetail.reliabilityChangeTitle'),
              t('agreementDetail.reliabilityChangeBody', {
                sign: diff > 0 ? '+' : '',
                diff,
                before: reliabilityBefore,
                after: reliabilityAfter,
              }),
            );
          }, 900);
        }
      }
    } catch (e) {
      const rawMsg = getErrorMessage(e, t('agreementDetail.actionFailed'));
      // Οι RPCs ελέγχουν το status της συμφωνίας πριν εκτελεστούν — αν κάποιος
      // άλλος πρόλαβε (π.χ. απάντησε πρώτος, ή η ενέργεια ξαναπατήθηκε κατά
      // λάθος) η βάση απορρίπτει με ένα τεχνικό μήνυμα. Δείχνουμε κάτι
      // κατανοητό και ξαναφορτώνουμε ώστε να φανεί η πραγματική κατάσταση.
      if (rawMsg.includes('Δεν εκκρεμεί αίτημα')) {
        setModalError(t('agreementDetail.staleActionState'));
        void load();
      } else {
        setModalError(rawMsg);
      }
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!agreement) {
    return (
      <View style={[styles.loader, { padding: spacing.lg }]}>
        <Text style={styles.notFound}>{t('agreements.notFound')}</Text>
        <Button
          title={t('common.back')}
          variant="secondary"
          onPress={() => (router.canGoBack() ? router.back() : router.replace('/(app)/agreements'))}
        />
      </View>
    );
  }

  // Όποιος έχει αποχωρήσει μονομερώς από συμφωνία πολλών ατόμων δεν πρέπει να
  // βλέπει πια το περιεχόμενο (ποσό/όρους/συμβαλλόμενους) — μένει στο
  // ιστορικό του, αλλά ανοίγοντάς την βλέπει μόνο ότι αποχώρησε.
  const myWithdrawnRow = multiParties.find((mp) => mp.user_id === user?.id && !!mp.withdrawn_at);
  if (myWithdrawnRow) {
    return (
      <View style={[styles.loader, { padding: spacing.lg, gap: spacing.md }]}>
        <Ionicons name="exit-outline" size={40} color={colors.textMuted} />
        <Text style={styles.reviewDoneTitle}>{agreement.title}</Text>
        <Text style={styles.notFound}>{t('agreementDetail.youWithdrewFromThisAgreement')}</Text>
        <Button
          title={t('common.back')}
          variant="secondary"
          onPress={() => (router.canGoBack() ? router.back() : router.replace('/(app)/agreements'))}
        />
      </View>
    );
  }

  const isCreator = agreement.creator_id === user?.id;
  const isActiveOrOverdue = agreement.status === 'active' || (agreement.status as string) === 'overdue';
  const creatorName = profiles[agreement.creator_id]?.full_name?.trim() || t('common.unknown');
  const isInvitePending = !agreement.counterparty_id && (!!agreement.counterparty_email || !!agreement.counterparty_name);
  // Όσο η πρόσκληση εκκρεμεί δείχνουμε το όνομα που έδωσε ο δημιουργός· μόλις
  // ο προσκεκλημένος εγγραφεί, υπερισχύει το όνομα του δικού του προφίλ.
  const counterpartyName = counterpartyDisplayName(
    agreement,
    agreement.counterparty_id ? profiles[agreement.counterparty_id]?.full_name : null,
    {
      invited: (v) => t('agreementDetail.invitedLabel', { name: v }),
      unknown: t('common.unknown'),
      empty: '—',
    },
  );

  const otherPartyId = isCreator ? agreement.counterparty_id : agreement.creator_id;
  const otherPartyName = isCreator ? counterpartyName : creatorName;
  // Η αξιολόγηση προτείνεται όποτε τελειώνει μια συνεργασία — είτε ολοκληρώθηκε
  // κανονικά, είτε έληξε μέσω επιλυμένης διαφωνίας (η ίδια η συμφωνία σε αυτή
  // την περίπτωση καταλήγει σε 'cancelled', όχι 'completed').
  const disputeConcluded = dispute?.status === 'resolved' || dispute?.status === 'auto_cancelled';
  const canReview =
    ((REVIEWABLE_STATUSES as readonly string[]).includes(agreement.status) || disputeConcluded)
    && !!otherPartyId
    && multiParties.length === 0;

  // ── Πολλών ατόμων: ουρά αξιολογήσεων, ένας συμβαλλόμενος τη φορά ──
  const multiDisputeConcluded = multiDispute?.status === 'resolved' || multiDispute?.status === 'auto_cancelled';
  const canReviewMulti =
    multiParties.length > 0
    && ((REVIEWABLE_STATUSES as readonly string[]).includes(agreement.status) || multiDisputeConcluded);
  const reviewedIds = new Set(myMultiReviews.map((r) => r.reviewee_id));
  const pendingReviewees = canReviewMulti
    ? multiParties
        .filter((mp) => mp.user_id && mp.user_id !== user?.id && mp.has_signed && !mp.withdrawn_at && !reviewedIds.has(mp.user_id))
        .map((mp) => ({ id: mp.user_id!, name: partyDisplayName(mp, profiles, t) }))
    : [];

  const myAgreed = isCreator ? agreement.creator_agreed : agreement.counterparty_agreed;
  const otherAgreed = isCreator ? agreement.counterparty_agreed : agreement.creator_agreed;

  const iRequestedCancel = agreement.cancel_requested_by === user?.id;
  const requesterName =
    agreement.cancel_requested_by === agreement.creator_id ? creatorName : counterpartyName;

  // Όταν εκκρεμεί κάποια απόφαση του χρήστη (υπογραφή/επιβεβαίωση πληρωμής/
  // ολοκλήρωσης/ακύρωσης), κρύβουμε τα δευτερεύοντα κουμπιά (Επικοινωνία, PDF)
  // στο footer ώστε το action box να μη μοιράζεται τον χώρο μαζί τους — λιγότερη
  // κάλυψη οθόνης, πιο εύκολο να διαβαστεί η συμφωνία από πάνω.
  const needsMyDecision =
    (multiParties.length === 0 && agreement.status === 'pending' && !myAgreed) ||
    (agreement.amount != null && !!agreement.payment_declared_by && agreement.payment_declared_by !== user?.id && !agreement.payment_confirmed_at) ||
    (agreement.status === 'pending_completion' && agreement.completion_requested_by !== user?.id) ||
    (agreement.status === 'cancel_requested' && !iRequestedCancel && multiParties.length === 0);

  const myWalletBalance = Number(myProfile?.wallet_balance ?? 0);
  const walletCoversFee = myWalletBalance >= 1;

  const wasPaidByMe = payments.some(
    (p) => p.user_id === user?.id && p.captured_at && p.status === 'paid',
  );
  const refundOnMutualLine = wasPaidByMe ? t('agreementDetail.refundOnMutual') : '';
  const refundIfMutualLine = wasPaidByMe ? t('agreementDetail.refundIfMutual') : '';
  const otherArticle = genderedArticle(otherPartyName);
  const requesterArticle = genderedArticle(requesterName);

  // Σε συμφωνίες πολλών ατόμων δεν υπάρχει ένας "άλλος συμβαλλόμενος" — το
  // όνομα του αιτούντος ολοκλήρωση πρέπει να βρεθεί από το completion_requested_by.
  const completionRequesterName = multiParties.length > 0
    ? (profiles[agreement.completion_requested_by ?? '']?.full_name?.trim() || t('agreementDetail.initiatorFallback'))
    : otherPartyName;
  const completionRequesterArticle = genderedArticle(completionRequesterName);

  const paymentDeclarerName = multiParties.length > 0
    ? (profiles[agreement.payment_declared_by ?? '']?.full_name?.trim() || t('agreementDetail.initiatorFallback'))
    : otherPartyName;
  const paymentDeclarerArticle = genderedArticle(paymentDeclarerName);

  type TagsInput = { options: { key: string; label: string }[]; selected: string[]; onToggle: (k: string) => void };
  const confirmConfigs: Record<
    PendingAction,
    {
      title: string;
      message?: string;
      bullets?: string[];
      warning?: string;
      confirmLabel: string;
      danger?: boolean;
      tags?: TagsInput;
    }
  > = {
    accept: {
      title: t('agreementDetail.acceptTitle'),
      message: t('agreementDetail.acceptMessage', {
        title: agreement.title,
        feeNote: isFree === false
          ? t(walletCoversFee ? 'agreementDetail.feeFromInk' : 'agreementDetail.feeChargeInk')
          : t('agreementDetail.feeFree'),
      }),
      confirmLabel: t('agreementDetail.acceptConfirmLabel'),
    },
    reject: {
      title: t('agreementDetail.rejectTitle'),
      message: t('agreementDetail.rejectMessage', { title: agreement.title, article: otherArticle, name: otherPartyName }),
      confirmLabel: t('agreementDetail.rejectConfirmLabel'),
      danger: true,
    },
    withdraw: {
      title: t('agreementDetail.withdrawTitle'),
      message: t('agreementDetail.withdrawMessage', { title: agreement.title, name: otherPartyName }),
      confirmLabel: t('agreementDetail.withdrawConfirmLabel'),
    },
    complete: {
      title: t('agreementDetail.completeTitle'),
      message: t('agreementDetail.completeMessage', { title: agreement.title }),
      confirmLabel: t('agreementDetail.completeConfirmLabel'),
    },
    terminate: {
      title: t('agreementDetail.terminateTitle'),
      message: t('agreementDetail.terminateMessage', { title: agreement.title, article: otherArticle, name: otherPartyName }),
      confirmLabel: t('agreementDetail.terminateConfirmLabel'),
    },
    cancel_request: {
      title: t('agreementDetail.cancelRequestTitle'),
      message: t('agreementDetail.cancelRequestMessage', { title: agreement.title, article: otherArticle, name: otherPartyName }) + refundIfMutualLine,
      confirmLabel: t('agreementDetail.cancelRequestConfirmLabel'),
      danger: true,
      tags: { options: CANCEL_TAGS_REQUESTER, selected: selectedTags, onToggle: toggleTag },
    },
    cancel_agree: {
      title: t('agreementDetail.cancelAgreeTitle'),
      message: t('agreementDetail.cancelAgreeMessage', { title: agreement.title, name: requesterName }) + refundOnMutualLine,
      confirmLabel: t('agreementDetail.cancelAgreeConfirmLabel'),
      tags: { options: CANCEL_TAGS_AGREE, selected: selectedTags, onToggle: toggleTag },
    },
    request_completion: {
      title: t('agreementDetail.requestCompletionTitle'),
      message: t('agreementDetail.requestCompletionMessage', { title: agreement.title, article: otherArticle, name: otherPartyName }),
      confirmLabel: t('agreementDetail.requestCompletionConfirmLabel'),
    },
    confirm_completion: {
      title: t('agreementDetail.confirmCompletionTitle'),
      message: t('agreementDetail.confirmCompletionMessage', { title: agreement.title, article: completionRequesterArticle, name: completionRequesterName }),
      confirmLabel: t('agreementDetail.confirmCompletionConfirmLabel'),
    },
    reject_completion: {
      title: t('agreementDetail.rejectCompletionTitle'),
      message: t('agreementDetail.rejectCompletionMessage', { title: agreement.title, article: completionRequesterArticle, name: completionRequesterName }),
      confirmLabel: t('agreementDetail.declineVerb'),
      danger: true,
    },
    declare_payment: {
      title: t('agreementDetail.declarePaymentTitle'),
      message: t('agreementDetail.declarePaymentMessage', { title: agreement.title, article: otherArticle, name: otherPartyName }),
      confirmLabel: t('agreementDetail.declarePaymentConfirmLabel'),
    },
    confirm_payment: {
      title: t('agreementDetail.confirmPaymentTitle'),
      message: t('agreementDetail.confirmPaymentMessage', { title: agreement.title, article: paymentDeclarerArticle, name: paymentDeclarerName }),
      warning: t('agreementDetail.confirmPaymentWarning'),
      confirmLabel: t('agreementDetail.confirmPaymentConfirmLabel'),
    },
    reject_payment: {
      title: t('agreementDetail.rejectPaymentTitle'),
      message: t('agreementDetail.rejectPaymentMessage', { title: agreement.title, article: paymentDeclarerArticle, name: paymentDeclarerName }),
      confirmLabel: t('agreementDetail.rejectPaymentConfirmLabel'),
      danger: true,
    },
    reject_cancel: {
      title: t('agreementDetail.rejectCancelTitle'),
      message: t('agreementDetail.rejectCancelMessage', { title: agreement.title, article: requesterArticle, name: requesterName }),
      confirmLabel: t('agreementDetail.rejectCancelConfirmLabel'),
    },
    multi_cancel_request: {
      title: t('agreementDetail.multiCancelRequestTitle'),
      message: t('agreementDetail.multiCancelRequestMessage'),
      confirmLabel: t('agreementDetail.multiCancelRequestConfirm'),
      danger: true,
    },
  };
  const activeConfig = pendingAction ? confirmConfigs[pendingAction] : null;

  // Καταγράφει τη θέση όποιου ActionBox (εκκρεμής απόφαση/αξιολόγηση) όντως
  // αποδίδεται μέσα στο scrollable footer, και κάνει αυτόματο scroll εκεί μία
  // φορά — ώστε ο χρήστης που ήρθε από notification να το βλέπει αμέσως,
  // χωρίς να ψάχνει. Το scroll γίνεται εδώ (όχι σε useEffect) γιατί το
  // onLayout είναι native event, όχι state change — δε θα ξαναγινόταν
  // re-render για να «ξυπνήσει» ένα useEffect.
  // Ίδια λογική με το markPendingActionLayout, αλλά για την Προκαταβολή/Δόσεις
  // — ζει στο κύριο scrollable περιεχόμενο (scrollRef), όχι στο footer, και η
  // «εκκρεμότητα» δεν είναι γνωστή στον γονέα παρά μόνο μέσω onPendingChange
  // (εσωτερικό state του AdvancePaymentSection) — τα δύο callbacks μπορεί να
  // έρθουν με όποια σειρά, γι' αυτό ελέγχουμε και τα δύο.
  function tryScrollToAdvancePayment() {
    if (scrolledToActionRef.current) return;
    if (!advancePaymentPendingRef.current) return;
    if (advancePaymentYRef.current == null) return;
    scrolledToActionRef.current = true;
    const y = advancePaymentYRef.current;
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ y, animated: true });
    });
  }

  function markPendingActionLayout(e: LayoutChangeEvent) {
    pendingActionYRef.current = e.nativeEvent.layout.y;
    if (scrolledToActionRef.current) return;
    scrolledToActionRef.current = true;
    const y = pendingActionYRef.current;
    requestAnimationFrame(() => {
      footerScrollRef.current?.scrollTo({ y, animated: true });
    });
  }

  return (
    <View style={styles.flex}>
      <ScrollView
        ref={scrollRef}
        contentContainerStyle={[
          styles.content,
          { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl },
        ]}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.primary}
            colors={[colors.primary]}
          />
        }
      >
        <View style={styles.topBar}>
          <Pressable
            onPress={() => (router.canGoBack() ? router.back() : router.replace('/(app)/agreements'))}
            hitSlop={12}
            accessibilityRole="button"
          >
            <Text style={styles.back}>{t('agreements.nav_back')}</Text>
          </Pressable>
        </View>

        <View style={styles.docHeader}>
          <Text style={styles.docId}>ΧΕΙΡ-{agreement.id.slice(0, 8).toUpperCase()}</Text>
          <Text style={styles.title}>{agreement.title}</Text>
          <View style={styles.headerMeta}>
            <StatusBadge status={agreement.status} />
            <Text style={styles.docDate}>{formatDate(agreement.created_at)}</Text>
          </View>
        </View>

        <View style={styles.card}>
          <Row
            label={t('agreementDetail.labelType')}
            value={
              (agreement as Agreement & { type?: string }).type === 'fixed_term' ? t('agreementDetail.typeFixedTerm') :
              (agreement as Agreement & { type?: string }).type === 'ongoing'    ? t('agreementDetail.typeOngoing') :
              t('agreementDetail.typeOneOff')
            }
          />
          {(agreement as Agreement & { type?: string }).type === 'fixed_term' ? (
            <Row
              label={t('agreementDetail.labelDuration')}
              value={(() => {
                const a = agreement as Agreement & { start_date?: string | null; end_date?: string | null };
                if (a.start_date && a.end_date) {
                  return `${formatDate(a.start_date)} – ${formatDate(a.end_date)}`;
                }
                return t('agreementDetail.notSet');
              })()}
            />
          ) : (agreement as Agreement & { type?: string }).type === 'ongoing' ? (
            <Row label={t('agreementDetail.labelDuration')} value={t('agreementDetail.typeOngoing')} />
          ) : (
            <Row
              label={agreement.expires_at_label ?? t('agreementDetail.labelDate')}
              value={agreement.expires_at ? formatDate(agreement.expires_at) : t('agreements.noExpiry')}
            />
          )}
          {agreement.amount_unit === 'percent' && multiParties.length === 0 ? (
            <Row
              label={t('agreements.field_percent')}
              value={t('agreementDetail.percentSplitValue', {
                creatorName: creatorName + (isCreator ? ` ${t('common.you')}` : ''),
                creatorPercent: agreement.amount ?? 0,
                counterpartyName: counterpartyName + (!isCreator ? ` ${t('common.you')}` : ''),
                counterpartyPercent: Math.round((100 - Number(agreement.amount ?? 0)) * 100) / 100,
              })}
            />
          ) : (
            <Row
              label={agreement.amount_unit === 'percent' ? t('agreements.field_percent') : t('agreements.field_amount')}
              value={formatAgreementAmount(agreement.amount, agreement.amount_unit, agreement.currency)}
            />
          )}
          {agreement.amount_unit !== 'percent' && (
            <View style={{ alignItems: 'flex-end', marginTop: -6 }}>
              <CurrencyHint amount={agreement.amount} currency={agreement.currency} />
            </View>
          )}
          <Row
            label={t('agreements.field_creator')}
            value={creatorName + (isCreator ? ` ${t('common.you')}` : '')}
            verified={!!profiles[agreement.creator_id]?.verified}
            levelInfo={partyLevels[agreement.creator_id]}
            onPress={
              !isCreator ? () => router.push({ pathname: '/(app)/users/[id]', params: { id: agreement.creator_id } }) : undefined
            }
          />
          {(() => {
            // Όταν ο δημιουργός ανήκει σε επιχείρηση με συνεταίρους, δείχνουμε
            // ποιος συγκεκριμένα από την επιχείρηση χειρίστηκε τη συμφωνία —
            // ώστε ο πελάτης/συνέταιροι να ξέρουν τον υπεύθυνο επικοινωνίας.
            const creatorProfile = profiles[agreement.creator_id] as (Profile & { business_id?: string | null; business_name?: string | null; show_business_name?: boolean | null }) | undefined;
            if (!creatorProfile?.business_id || !creatorProfile?.show_business_name || !creatorProfile?.business_name?.trim()) return null;
            return (
              <Row
                label={t('agreementDetail.dealOwnerLabel')}
                value={t('agreementDetail.dealOwnerValue', { name: creatorName, business: creatorProfile.business_name.trim() })}
              />
            );
          })()}
          {multiParties.length === 0 && (
            <Row
              label={t('agreements.field_counterparty')}
              value={counterpartyName + (!isCreator ? ` ${t('common.you')}` : '')}
              verified={
                !!agreement.counterparty_id && !!profiles[agreement.counterparty_id]?.verified
              }
              levelInfo={agreement.counterparty_id ? partyLevels[agreement.counterparty_id] : undefined}
              onPress={
                isCreator && agreement.counterparty_id
                  ? () => router.push({ pathname: '/(app)/users/[id]', params: { id: agreement.counterparty_id! } })
                  : undefined
              }
            />
          )}
        </View>

        {multiParties.length > 0 && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>{t('agreementDetail.partiesCount', { count: multiParties.length })}</Text>
            <View style={styles.dividerLine} />
            {multiParties.map((mp) => {
              const withdrawn = !!mp.withdrawn_at;
              return (
                <View key={mp.id} style={styles.partyRow}>
                  <Text style={[styles.partyName, withdrawn && styles.partyNameWithdrawn]} numberOfLines={1}>
                    {partyDisplayName(mp, profiles, t)} {mp.role === 'creator' ? t('agreementDetail.creatorTag') : ''}
                  </Text>
                  {withdrawn ? (
                    <Text style={styles.partyWithdrawnTag}>{t('agreementDetail.multiPartyWithdrawnTag')}</Text>
                  ) : (
                    <>
                      <Text style={styles.partyShare}>
                        {mp.share_percent}%
                        {agreement.amount != null ? ` · ${formatAmount((agreement.amount * mp.share_percent) / 100, agreement.currency)}` : ''}
                      </Text>
                      {agreement.status === 'cancel_requested' ? (
                        <Ionicons
                          name={mp.cancel_vote ? 'checkmark-circle' : 'time-outline'}
                          size={16}
                          color={mp.cancel_vote ? colors.success : colors.textMuted}
                        />
                      ) : (
                        <Ionicons name={mp.has_signed ? 'checkmark-circle' : 'time-outline'} size={16} color={mp.has_signed ? colors.success : colors.textMuted} />
                      )}
                    </>
                  )}
                </View>
              );
            })}

            {agreement.status === 'pending' && multiParties.some((mp) => mp.user_id === user?.id && !mp.has_signed) && (
              <Button title={t('agreements.sign_btn')} icon="create-outline" onPress={onSignMultiParty} loading={multiBusy} />
            )}
            {agreement.status === 'pending' && multiParties.some((mp) => mp.user_id === user?.id && mp.has_signed) && multiParties.some((mp) => !mp.has_signed) && (
              <Text style={styles.waiting}>{t('agreementDetail.multiSignedWaitingOthers')}</Text>
            )}

            {!!multiDispute && multiDispute.status === 'open' && multiDispute.accused_user_id === user?.id && (
              <View style={styles.actionRow}>
                <View style={styles.actionBtn}>
                  <Button title={t('agreementDetail.acceptBlame')} accentColor={colors.warning} onPress={() => onRespondMultiParty(true)} loading={multiBusy} />
                </View>
                <View style={styles.actionBtn}>
                  <Button title={t('agreementDetail.disagree')} variant="secondary" accentColor={colors.danger} onPress={() => { setDisagreeStep(1); setShowDisagreeModal(true); }} loading={multiBusy} />
                </View>
              </View>
            )}

            {!!multiDispute && multiDispute.status === 'open' && multiDispute.initiated_by === user?.id && (
              <Text style={styles.waiting}>{t('agreementDetail.waitingAccusedResponse')}</Text>
            )}

            {!!multiDispute && multiDispute.status === 'voting' && (
              multiDispute.initiated_by === user?.id || multiDispute.accused_user_id === user?.id ? (
                <Text style={styles.waiting}>{t('agreementDetail.othersVoting')}</Text>
              ) : (
                <View style={{ gap: spacing.xs }}>
                  <Text style={styles.sectionTitle}>{t('agreementDetail.voteWhoIsRight')}</Text>
                  {(() => {
                    const initiatorStatement = disputeStatements.find((s) => s.user_id === multiDispute.initiated_by);
                    const accusedStatement = disputeStatements.find((s) => s.user_id === multiDispute.accused_user_id);
                    const initiatorTags = initiatorStatement?.tags ?? [];
                    const accusedTags = accusedStatement?.tags ?? [];
                    return (
                      <View style={{ gap: spacing.xs, marginBottom: spacing.xs }}>
                        <View>
                          <Text style={styles.waiting}>
                            {t('agreementDetail.initiatorSays', { name: profiles[multiDispute.initiated_by]?.full_name || t('agreementDetail.initiatorFallback') })}{' '}
                            {initiatorTags.length > 0 ? initiatorTags.map(disputeTagLabel).join(', ') : '—'}
                          </Text>
                          {!!initiatorStatement?.statement && (
                            <Text style={styles.statementText}>{initiatorStatement.statement}</Text>
                          )}
                        </View>
                        <View>
                          <Text style={styles.waiting}>
                            {t('agreementDetail.accusedReplies', { name: profiles[multiDispute.accused_user_id!]?.full_name || t('agreementDetail.accusedFallback') })}{' '}
                            {accusedTags.length > 0 ? accusedTags.map(disputeTagLabel).join(', ') : '—'}
                          </Text>
                          {!!accusedStatement?.statement && (
                            <Text style={styles.statementText}>{accusedStatement.statement}</Text>
                          )}
                        </View>
                      </View>
                    );
                  })()}
                  {(() => {
                    const myVote = disputeVotes.find((v) => v.voter_id === user?.id);
                    if (myVote) {
                      const votedName = myVote.vote_for === multiDispute.initiated_by
                        ? (profiles[multiDispute.initiated_by]?.full_name || t('agreementDetail.initiatorFallback'))
                        : myVote.vote_for === multiDispute.accused_user_id
                          ? (profiles[multiDispute.accused_user_id!]?.full_name || t('agreementDetail.accusedFallback'))
                          : t('agreementDetail.neutralVote');
                      return <Text style={styles.waiting}>{t('agreementDetail.voteAlreadyCast', { name: votedName })}</Text>;
                    }
                    return (
                      <>
                        <View style={styles.actionRow}>
                          <View style={styles.actionBtn}>
                            <Button title={profiles[multiDispute.initiated_by]?.full_name || t('agreementDetail.initiatorFallback')} onPress={() => onCastVote(multiDispute.initiated_by)} loading={multiBusy} />
                          </View>
                          <View style={styles.actionBtn}>
                            <Button title={profiles[multiDispute.accused_user_id!]?.full_name || t('agreementDetail.accusedFallback')} onPress={() => onCastVote(multiDispute.accused_user_id)} loading={multiBusy} />
                          </View>
                        </View>
                        <Button title={t('agreementDetail.neutralVote')} variant="secondary" onPress={() => onCastVote(null)} loading={multiBusy} />
                      </>
                    );
                  })()}
                </View>
              )
            )}

            {!!multiDispute && multiDispute.status === 'jury_review' && (
              multiDispute.initiated_by === user?.id || multiDispute.accused_user_id === user?.id ? (
                <Text style={styles.waiting}>{t('agreementDetail.juryReviewWaiting')}</Text>
              ) : (
                <Text style={styles.waiting}>{t('agreementDetail.juryReviewBystander')}</Text>
              )
            )}

            {!!multiDispute && multiDispute.status === 'admin_review' && (
              <Text style={styles.waiting}>{t('agreementDetail.noMajorityAdminReview')}</Text>
            )}

            {!!multiDispute && multiDispute.status === 'resolved' && (
              <Text style={styles.waiting}>
                {multiDispute.outcome === 'evidence_based'
                  ? t('agreementDetail.outcomeEvidenceBased')
                  : multiDispute.outcome === 'vote_majority'
                    ? t('agreementDetail.outcomeVoteMajority')
                    : multiDispute.outcome === 'penalty_respondent'
                      ? t('agreementDetail.outcomePenaltyRespondent')
                      : t('agreementDetail.outcomeDefault')}
              </Text>
            )}

            {agreement.status === 'cancel_requested' && (() => {
              const myRow = multiParties.find((mp) => mp.user_id === user?.id);
              const agreedCount = multiParties.filter((mp) => mp.cancel_vote).length;
              if (myRow?.cancel_vote) {
                return (
                  <Text style={styles.waiting}>
                    {t('agreementDetail.multiCancelWaitingOthers', { agreed: agreedCount, total: multiParties.length })}
                  </Text>
                );
              }
              return (
                <View style={{ gap: spacing.xs }}>
                  <Text style={styles.waiting}>
                    {t('agreementDetail.multiCancelRequestedBy', { name: requesterName, agreed: agreedCount, total: multiParties.length })}
                  </Text>
                  <View style={styles.actionRow}>
                    <View style={styles.actionBtn}>
                      <Button title={t('agreementDetail.cancelAgreeConfirmLabel')} accentColor="#F97316" onPress={() => onRespondMultiCancel(true)} loading={multiBusy} />
                    </View>
                    <View style={styles.actionBtn}>
                      <Button
                        title={t('agreementDetail.doNotWantCancel')}
                        variant="secondary"
                        accentColor="#F97316"
                        onPress={() => onRespondMultiCancel(false)}
                        loading={multiBusy}
                      />
                    </View>
                  </View>
                </View>
              );
            })()}
          </View>
        )}

        <View style={styles.card}>
          <Text style={styles.sectionTitle}>{t('agreements.section_terms')}</Text>
          <View style={styles.dividerLine} />
          <Text style={styles.commitment}>
            {multiParties.length > 0
              ? t('agreementDetail.multiCommitmentIntro', { names: multiParties.map((mp) => partyDisplayName(mp, profiles, t)).join(', ') })
              : commitmentIntro(creatorName, counterpartyName)}
          </Text>
          {!!agreement.description && (
            <Text style={styles.description}>{agreement.description}</Text>
          )}
        </View>

        {/* Αίτημα Επεξεργασίας — μικρό, διακριτικό κουμπί ακριβώς κάτω από τους όρους */}
        {(agreement.status === 'active' || (agreement.status as string) === 'overdue') && (() => {
          const ext = agreement as Agreement & {
            edit_requested_by?: string | null;
            edit_request_note?: string | null;
            edit_request_status?: string | null;
            edit_awaiting_final_approval?: boolean;
          };
          const editStatus = ext.edit_request_status ?? null;
          const iAmRequester = ext.edit_requested_by === user?.id;

          if (ext.edit_awaiting_final_approval) {
            // Ο δημιουργός αποθήκευσε αλλαγές — ο counterparty εγκρίνει/απορρίπτει
            if (!isCreator) {
              return (
                <ActionBox title={t('agreementDetail.creatorSavedChangesApprove')}>
                  <View style={styles.actionRow}>
                    <View style={styles.actionBtn}>
                      <Button title={t('agreementDetail.approve')} icon="checkmark-outline" onPress={() => onFinalizeEdit(true)} loading={editBusy} />
                    </View>
                    <View style={styles.actionBtn}>
                      <Button title={t('agreementDetail.rejectConfirmLabel')} variant="secondary" icon="close-outline" onPress={() => onFinalizeEdit(false)} loading={editBusy} />
                    </View>
                  </View>
                </ActionBox>
              );
            }
            return <Text style={styles.waiting}>{t('agreementDetail.waitingFinalApproval', { name: otherPartyName })}</Text>;
          }

          if (editStatus === 'pending') {
            if (iAmRequester) {
              return <Text style={styles.waiting}>{t('agreementDetail.editRequestPendingApproval', { name: otherPartyName })}</Text>;
            }
            return (
              <ActionBox title={t('agreementDetail.editRequestTitle', { note: ext.edit_request_note ?? '' })}>
                <View style={styles.actionRow}>
                  <View style={styles.actionBtn}>
                    <Button title={t('agreementDetail.approvalNoun')} icon="checkmark-outline" onPress={() => onRespondEditRequest(true)} loading={editBusy} />
                  </View>
                  <View style={styles.actionBtn}>
                    <Button title={t('common.reject')} variant="secondary" icon="close-outline" onPress={() => onRespondEditRequest(false)} loading={editBusy} />
                  </View>
                </View>
              </ActionBox>
            );
          }

          if (editStatus === 'approved') {
            if (isCreator) {
              return (
                <Button title={t('agreementDetail.editAgreementTitle')} icon="create-outline" small onPress={openEditForm} />
              );
            }
            return <Text style={styles.waiting}>{t('agreementDetail.editApprovedWaitingChanges', { name: otherPartyName })}</Text>;
          }

          // Καμία εκκρεμότητα → μικρό, διακριτικό κουμπί αιτήματος
          return (
            <Pressable style={styles.editRequestLink} onPress={() => setShowEditRequestModal(true)} accessibilityRole="button">
              <Ionicons name="create-outline" size={13} color={colors.textMuted} />
              <Text style={styles.editRequestLinkText}>{t('agreementDetail.editRequestLinkLabel')}</Text>
            </Pressable>
          );
        })()}

        {(agreement as any).include_professional_terms !== false && !!(profiles[agreement.creator_id] as any)?.professional_terms?.trim() && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>{t('agreementDetail.termsAndConditionsOf', { name: creatorName })}</Text>
            <View style={styles.dividerLine} />
            <Text style={styles.description}>{(profiles[agreement.creator_id] as any).professional_terms}</Text>
          </View>
        )}

        {/* Ψηφιακές υπογραφές */}
        {multiParties.length > 0 ? (
          multiParties.some((mp) => mp.has_signed) && (
            <SensitiveContent style={[styles.card, styles.signatureCard]}>
              <Text style={styles.sectionTitle}>{t('agreements.section_signatures')}</Text>
              <View style={styles.dividerLine} />
              {multiParties.filter((mp) => mp.has_signed && mp.user_id).map((mp) => (
                <SignatureRow
                  key={mp.user_id!}
                  name={partyDisplayName(mp, profiles, t)}
                  signature={profiles[mp.user_id!]?.signature_text}
                  signatureSvg={profiles[mp.user_id!]?.signature_svg}
                  verified={!!profiles[mp.user_id!]?.verified}
                  agreedAt={mp.signed_at}
                  code={signatureCode(agreement.id, mp.user_id!, mp.signed_at)}
                />
              ))}
              {multiParties.every((mp) => mp.has_signed) && (
                <Text style={styles.signedSeal}>
                  {t('agreementDetail.signedByAllParties')}
                </Text>
              )}
            </SensitiveContent>
          )
        ) : (agreement.creator_agreed || agreement.counterparty_agreed) && (
          <SensitiveContent style={[styles.card, styles.signatureCard]}>
            <Text style={styles.sectionTitle}>{t('agreements.section_signatures')}</Text>
            <View style={styles.dividerLine} />
            {agreement.creator_agreed && (
              <SignatureRow
                name={creatorName}
                signature={profiles[agreement.creator_id]?.signature_text}
                signatureSvg={profiles[agreement.creator_id]?.signature_svg}
                verified={!!profiles[agreement.creator_id]?.verified}
                agreedAt={agreement.creator_agreed_at}
                code={signatureCode(agreement.id, agreement.creator_id, agreement.creator_agreed_at)}
              />
            )}
            {agreement.counterparty_agreed && agreement.counterparty_id && (
              <SignatureRow
                name={counterpartyName}
                signature={profiles[agreement.counterparty_id]?.signature_text}
                signatureSvg={profiles[agreement.counterparty_id]?.signature_svg}
                verified={!!profiles[agreement.counterparty_id]?.verified}
                agreedAt={agreement.counterparty_agreed_at}
                code={signatureCode(
                  agreement.id,
                  agreement.counterparty_id,
                  agreement.counterparty_agreed_at,
                )}
              />
            )}
            {agreement.creator_agreed && agreement.counterparty_agreed && (
              <Text style={styles.signedSeal}>
                {t('agreements.signedByBoth')}
              </Text>
            )}
          </SensitiveContent>
        )}

        {/* Προκαταβολή — μόνο σε ενεργή συμφωνία με χρηματικό ποσό (€). Παραμένει
            ορατή (χωρίς κουμπιά ενεργειών) και μετά την επιβεβαίωση εξόφλησης,
            όσο η συμφωνία δεν έχει ακόμα ολοκληρωθεί ρητά. Δεν έχει νόημα σε
            συμφωνίες πολλών ατόμων — εκεί το ποσό είναι πάντα ποσοστιαία
            μοιρασιά μεταξύ όλων, όχι οφειλή ενός μέρους προς ένα άλλο. */}
        {(isActiveOrOverdue || agreement.status === 'pending_completion' || agreement.status === 'completed') &&
          agreement.amount != null &&
          agreement.amount_unit !== 'percent' &&
          multiParties.length === 0 &&
          user && (
            <View onLayout={(e) => { advancePaymentYRef.current = e.nativeEvent.layout.y; tryScrollToAdvancePayment(); }}>
              <AdvancePaymentSection
                agreement={agreement}
                userId={user.id}
                otherName={otherPartyName}
                onDeclarePayment={() => setPendingAction('declare_payment')}
                onAccepted={() => setShowMoneyHandoff(true)}
                onPendingChange={(hasPending) => {
                  advancePaymentPendingRef.current = hasPending;
                  if (hasPending) tryScrollToAdvancePayment();
                }}
              />
            </View>
          )}

        {/* Αίτημα ακύρωσης σε εκκρεμότητα — μόνο για τον αιτούντα (ο άλλος βλέπει το actionBox) */}
        {agreement.status === 'cancel_requested' && iRequestedCancel && (
          <View style={[styles.notice, styles.noticeWaiting]}>
            <Text style={styles.noticeText}>
              {t('agreements.cancelRequestedWaiting', { name: otherPartyName })}
            </Text>
          </View>
        )}

        {/* Completion notices */}
        {agreement.status === 'pending_completion' && agreement.completion_requested_by === user?.id && (
          <View style={[styles.notice, styles.noticeWaiting]}>
            <Text style={styles.noticeText}>
              {t('agreementDetail.completionRequestSent48h', { name: otherPartyName })}
            </Text>
          </View>
        )}
        {isActiveOrOverdue && agreement.payment_declared_by === user?.id && !agreement.payment_confirmed_at && (
          <View style={[styles.notice, styles.noticeWaiting]}>
            <Text style={styles.noticeText}>
              {t('agreements.paymentDeclaredWaiting', { name: otherPartyName })}
            </Text>
          </View>
        )}

        {/* Λόγοι ακύρωσης (ετικέτες) */}
        {((agreement.cancel_tags?.length ?? 0) > 0 || (agreement.cancel_response_tags?.length ?? 0) > 0) && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>{t('agreements.section_cancelReasons')}</Text>
            {(agreement.cancel_tags?.length ?? 0) > 0 && (
              <View style={styles.cancelTagsBlock}>
                <Text style={styles.reasonWho}>{requesterName}:</Text>
                <View style={styles.cancelTagsList}>
                  {(agreement.cancel_tags ?? []).map((k) => (
                    <View key={k} style={styles.cancelTag}>
                      <Text style={styles.cancelTagText}>{cancelTagLabel(k)}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
            {(agreement.cancel_response_tags?.length ?? 0) > 0 && (
              <View style={styles.cancelTagsBlock}>
                <Text style={styles.reasonWho}>{t('agreements.cancelResponse')}</Text>
                <View style={styles.cancelTagsList}>
                  {(agreement.cancel_response_tags ?? []).map((k) => (
                    <View key={k} style={styles.cancelTag}>
                      <Text style={styles.cancelTagText}>{cancelTagLabel(k)}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
          </View>
        )}

        {/* Δωρεάν αποστολή */}
        {isCreator && agreement.status === 'draft' && (
          <View style={styles.notice}>
            <Text style={styles.noticeText}>
              {t('agreementDetail.freeSendNotice')}
            </Text>
          </View>
        )}

        {/* Αμοιβαία αποδοχή σε εκκρεμότητα — μόνο αν έχεις ήδη υπογράψει (ο άλλος βλέπει το actionBox) */}
        {multiParties.length === 0 && agreement.status === 'pending' && !isInvitePending && myAgreed && (
          <View style={[styles.notice, styles.noticeWaiting]}>
            <Text style={styles.noticeText}>
              {agreement.from_offer && !isCreator
                ? t('agreementDetail.signedFreeWaitingActivation', { name: otherPartyName })
                : t('agreements.agreedWaiting', { name: otherPartyName })}
            </Text>
          </View>
        )}

        {/* Πρόσκληση μη-εγγεγραμμένου αντισυμβαλλόμενου */}
        {isCreator && agreement.status === 'pending' && isInvitePending && (
          <View style={[styles.notice, styles.noticeWaiting]}>
            <Text style={styles.noticeText}>
              {t('agreementDetail.inviteSentAutoLink', { email: agreement.counterparty_email })}
            </Text>
          </View>
        )}

        {/* Χρεώσεις (δεσμεύσεις/χρεώσεις 1€) */}
        {payments.length > 0 && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>{t('agreements.section_charges')}</Text>
            {payments.map((p) => {
              const label = holdLabel(p, t);
              return (
                <View key={p.id} style={styles.paymentRow}>
                  <View style={styles.paymentLeft}>
                    <Text style={styles.paymentAmount}>{formatAmount(p.amount, p.currency)}</Text>
                    <Text style={styles.paymentDate}>{formatDateTime(p.created_at)}</Text>
                  </View>
                  <Text style={[styles.paymentStatus, { color: label.color }]}>{label.text}</Text>
                </View>
              );
            })}
          </View>
        )}

        {/* Dispute banner (μόνο 2-ατόμων συμφωνίες) */}
        {!!dispute && multiParties.length === 0 && (
          <DisputeStatusBanner dispute={dispute} otherName={otherPartyName} />
        )}

        {/* Renewal banner — εμφανίζεται όταν το fixed_term agreement έχει ειδοποιηθεί για ανανέωση */}
        {(agreement as Agreement & { type?: string; renewal_notified_at?: string | null; creator_wants_renewal?: boolean | null; counterparty_wants_renewal?: boolean | null }).type === 'fixed_term' &&
         !!(agreement as Agreement & { renewal_notified_at?: string | null }).renewal_notified_at &&
         agreement.status === 'active' && (
          <RenewalBanner
            agreementId={agreement.id}
            isCreator={isCreator}
            creatorWants={(agreement as Agreement & { creator_wants_renewal?: boolean | null }).creator_wants_renewal ?? null}
            counterpartyWants={(agreement as Agreement & { counterparty_wants_renewal?: boolean | null }).counterparty_wants_renewal ?? null}
            onRenewed={(newId) => router.replace({ pathname: '/(app)/agreements/[id]', params: { id: newId } })}
            onDeclined={() => setAgreement((prev) => prev ? { ...prev, status: 'completed' } : prev)}
          />
        )}

      </ScrollView>

      {hasUpdate && (
        <Pressable
          style={[styles.updateBubble, { bottom: footerHeight + insets.bottom + spacing.sm }]}
          onPress={() => { setHasUpdate(false); void load(); }}
        >
          <Ionicons name="refresh" size={16} color="#fff" />
          <Text style={styles.updateBubbleText}>{t('agreementDetail.newStateBubble')}</Text>
        </Pressable>
      )}

      {/* Πάτημα οπουδήποτε πάνω από το footer κλείνει το μεγαλωμένο icon-button, αν υπάρχει */}
      {expandedActionKey && (
        <Pressable
          style={[StyleSheet.absoluteFillObject, { bottom: footerHeight }]}
          onPress={() => setExpandedActionKey(null)}
        />
      )}

      {/* Σταθερό footer με όλα τα κουμπιά ενεργειών — πάντα ορατό, χωρίς scroll */}
      <View
        style={[styles.footerBar, { paddingBottom: insets.bottom + spacing.sm }]}
        onLayout={(e) => setFooterHeight(e.nativeEvent.layout.height)}
      >
        <ErrorText>{error}</ErrorText>
        <ScrollView ref={footerScrollRef} contentContainerStyle={styles.actions} showsVerticalScrollIndicator={false}>
          {isCreator && agreement.status === 'draft' && (
            <>
              <Button
                title={t('agreements.action_editDraft')}
                icon="create-outline"
                variant="secondary"
                onPress={() => router.push({ pathname: '/(app)/agreements/edit', params: { id: agreement.id } })}
              />
              <Button title={t('common.send')} icon="paper-plane-outline" onPress={sendAgreement} loading={busy} />
            </>
          )}

          {multiParties.length === 0 && agreement.status === 'pending' && !myAgreed && (
            <ActionBox title={t('agreements.action_signatureRequired')} onLayout={markPendingActionLayout}>
              <Pressable
                style={({ pressed }) => [
                  styles.acceptBtn,
                  styles.acceptBtnOrange,
                  pressed && !busy && styles.acceptPressed,
                  busy && styles.acceptDisabled,
                ]}
                onPress={() => setPendingAction('accept')}
                disabled={busy}
                accessibilityRole="button"
              >
                {busy ? (
                  <ActivityIndicator color={colors.textOnPrimary} />
                ) : (
                  <Text style={styles.acceptLabel}>
                    {isFree === true
                      ? t('agreements.action_signFree')
                      : isFree === false
                        ? t('agreements.action_signInk')
                        : t('agreements.action_sign')}
                  </Text>
                )}
              </Pressable>
              <Button
                title={t('common.reject')}
                variant="secondary"
                accentColor="#F97316"
                onPress={() => setPendingAction('reject')}
              />
            </ActionBox>
          )}

          {/* Επικοινωνία εκτός ενεργής κατάστασης (π.χ. pending/completed/cancelled) —
              στις ενεργές καταστάσεις το κουμπί επικοινωνίας φαίνεται ήδη δίπλα στο
              κουμπί Αναφοράς Διαφωνίας παρακάτω, οπότε δεν το ξαναδείχνουμε εδώ. */}
          {!needsMyDecision && !isActiveOrOverdue && multiParties.length === 0 && agreement.status !== 'draft'
            && (isCreator ? agreement.counterparty_agreed : agreement.creator_agreed) && (
            <ChatBubble inline otherUserId={otherPartyId} label={otherPartyName} agreementId={agreement.id} />
          )}
          {!needsMyDecision && !isActiveOrOverdue && multiParties.length > 0 && agreement.status !== 'draft' && (() => {
            const chatTargets = multiParties
              .filter((mp) => mp.user_id && mp.user_id !== user?.id && mp.has_signed)
              .map((mp) => ({ mp, profile: profiles[mp.user_id!] }))
              .filter(({ profile }) => getAvailableApps(profile).length > 0)
              .map(({ mp, profile }) => ({
                userId: mp.user_id!,
                label: partyDisplayName(mp, profiles, t),
                phone: profile?.phone ?? null,
                communication_apps: profile?.communication_apps,
              }));
            return <ChatBubble inline targets={chatTargets} agreementId={agreement.id} />;
          })()}

          {multiParties.length === 0 && agreement.status === 'pending' && myAgreed && !otherAgreed && (
            <>
              <Text style={styles.waiting}>{t('agreements.agreedWaitingOther', { name: otherPartyName })}</Text>
              <Button
                title={t('agreements.action_withdrawProposal')}
                variant="secondary"
                onPress={() => setPendingAction('withdraw')}
              />
            </>
          )}

          {/* Κοινοποίηση/επαναποστολή link πρόσκλησης — όσο η συμφωνία είναι σε
              αναμονή και ο άλλος δεν έχει υπογράψει ακόμα, ανεξάρτητα από το αν
              έχει ήδη συνδεθεί ο λογαριασμός του (π.χ. δεν έλαβε ειδοποίηση). */}
          {multiParties.length === 0 && agreement.status === 'pending' && !otherAgreed && isCreator && (
            <Button
              title={agreement.counterparty_id ? t('agreements.action_resendInviteLink') : t('agreements.action_shareLink')}
              icon="share-outline"
              variant="secondary"
              onPress={() => void onShareLink()}
            />
          )}

          {isActiveOrOverdue && (
            <>
              {/* Όλα τα δευτερεύοντα κουμπιά μαζί, μία σταθερή γραμμή στο footer:
                  ολοκλήρωση/τερματισμός, ακύρωση (2-ατόμων ή αίτημα/αποχώρηση
                  πολλαπλών), αναφορά διαφωνίας, επικοινωνία. */}
              {(!agreement.payment_declared_by || !!agreement.payment_confirmed_at) && !needsMyDecision && (() => {
                const chatTargets = multiParties
                  .filter((mp) => mp.user_id && mp.user_id !== user?.id && mp.has_signed)
                  .map((mp) => ({ mp, profile: profiles[mp.user_id!] }))
                  .filter(({ profile }) => getAvailableApps(profile).length > 0)
                  .map(({ mp, profile }) => ({
                    userId: mp.user_id!,
                    label: partyDisplayName(mp, profiles, t),
                    phone: profile?.phone ?? null,
                    communication_apps: profile?.communication_apps,
                  }));
                const showMultiDispute = multiParties.length > 0 && !multiDispute && multiParties.length > 2;
                const myMultiRow = multiParties.find((mp) => mp.user_id === user?.id);
                const canWithdraw = multiParties.length > 0 && !!myMultiRow && myMultiRow.role !== 'creator';
                return (
                  <IconActionRow expandedKey={expandedActionKey} onRequestExpand={setExpandedActionKey}>
                    <IconActionButton
                      actionKey="complete"
                      icon="checkmark-circle-outline"
                      label={(agreement as Agreement & { type?: string }).type === 'ongoing'
                        ? t('agreementDetail.endOngoingAgreementBtn')
                        : t('agreementDetail.completeAgreementBtn')}
                      shortLabel={(agreement as Agreement & { type?: string }).type === 'ongoing' ? 'Τερματ.' : 'Ολοκλ.'}
                      color={colors.primary}
                      onPress={() => setPendingAction('request_completion')}
                      expandedKey={expandedActionKey}
                      onRequestExpand={setExpandedActionKey}
                    />
                    {multiParties.length === 0 && !agreement.cancel_requested_by && (
                      <IconActionButton
                        actionKey="cancel"
                        icon="close-circle-outline"
                        label={t('agreementDetail.cancelAgreementBtn')}
                        shortLabel="Ακύρωση"
                        color="#F97316"
                        onPress={() => setPendingAction('cancel_request')}
                        expandedKey={expandedActionKey}
                        onRequestExpand={setExpandedActionKey}
                      />
                    )}
                    {multiParties.length > 0 && (
                      <IconActionButton
                        actionKey="multi-cancel"
                        icon="close-circle-outline"
                        label={t('agreementDetail.multiCancelRequestBtn')}
                        shortLabel="Ακύρωση"
                        color="#F97316"
                        onPress={onRequestMultiCancel}
                        loading={multiBusy}
                        expandedKey={expandedActionKey}
                        onRequestExpand={setExpandedActionKey}
                      />
                    )}
                    {canWithdraw && (
                      <IconActionButton
                        actionKey="multi-withdraw"
                        icon="exit-outline"
                        label={t('agreementDetail.multiWithdrawBtn')}
                        shortLabel="Αποχώρ."
                        color="#EF4444"
                        onPress={onWithdrawMultiParty}
                        loading={multiBusy}
                        expandedKey={expandedActionKey}
                        onRequestExpand={setExpandedActionKey}
                      />
                    )}
                    {(multiParties.length === 0 || showMultiDispute) && (
                      <IconActionButton
                        actionKey="dispute"
                        icon="alert-circle-outline"
                        label={t('agreements.action_reportDispute')}
                        shortLabel="Διαφωνώ"
                        color={colors.danger}
                        onPress={() => (multiParties.length === 0 ? setDisputeFlow('open') : setShowAccuseModal(true))}
                        expandedKey={expandedActionKey}
                        onRequestExpand={setExpandedActionKey}
                      />
                    )}
                    {multiParties.length === 0 ? (
                      <ChatBubble
                        inline
                        otherUserId={otherPartyId}
                        label={otherPartyName}
                        agreementId={agreement.id}
                        expandedKey={expandedActionKey}
                        onRequestExpand={setExpandedActionKey}
                      />
                    ) : (
                      <ChatBubble
                        inline
                        targets={chatTargets}
                        agreementId={agreement.id}
                        expandedKey={expandedActionKey}
                        onRequestExpand={setExpandedActionKey}
                      />
                    )}
                  </IconActionRow>
                );
              })()}

              {/* Payment flow — το κουμπί Δήλωση Εξόφλησης εμφανίζεται μέσα στο AdvancePaymentSection */}
              {agreement.amount != null &&
                agreement.payment_declared_by &&
                agreement.payment_declared_by !== user?.id &&
                !agreement.payment_confirmed_at && (
                  <ActionBox title={t('agreementDetail.paymentDeclaredConfirmQuestion', { article: paymentDeclarerArticle, name: paymentDeclarerName })} onLayout={markPendingActionLayout}>
                    <View style={styles.actionRow}>
                      <View style={styles.actionBtn}>
                        <Button
                          title={t('agreementDetail.confirmVerb')}
                          icon="checkmark-outline"
                          accentColor="#F97316"
                          onPress={() => setPendingAction('confirm_payment')}
                        />
                      </View>
                      <View style={styles.actionBtn}>
                        <Button
                          title={t('agreementDetail.declineVerb')}
                          icon="close-outline"
                          variant="secondary"
                          accentColor="#F97316"
                          onPress={() => setPendingAction('reject_payment')}
                        />
                      </View>
                    </View>
                  </ActionBox>
                )}
              {agreement.payment_declared_by === user?.id && !agreement.payment_confirmed_at && (
                <Text style={styles.waiting}>
                  {t('agreementDetail.waitingPaymentConfirmation', { name: otherPartyName })}
                </Text>
              )}
            </>
          )}

          {/* Αίτημα ολοκλήρωσης: ο Β επιβεβαιώνει ή απορρίπτει */}
          {agreement.status === 'pending_completion' &&
            agreement.completion_requested_by !== user?.id && (
              <ActionBox title={t('agreementDetail.completionRequestConfirmQuestion', { article: completionRequesterArticle, name: completionRequesterName })} onLayout={markPendingActionLayout}>
                <View style={styles.actionRow}>
                  <View style={styles.actionBtn}>
                    <Button
                      title={t('common.yes')}
                      icon="checkmark-done-outline"
                      accentColor="#F97316"
                      onPress={() => setPendingAction('confirm_completion')}
                    />
                  </View>
                  <View style={styles.actionBtn}>
                    <Button
                      title={t('common.no')}
                      icon="close-outline"
                      variant="secondary"
                      accentColor="#F97316"
                      onPress={() => setPendingAction('reject_completion')}
                    />
                  </View>
                </View>
              </ActionBox>
            )}

          {/* Ροή ακύρωσης: ο αιτών περιμένει */}
          {agreement.status === 'cancel_requested' && iRequestedCancel && multiParties.length === 0 && (
            <Text style={styles.waiting}>{t('agreements.cancelWaiting')}</Text>
          )}

          {/* Ροή ακύρωσης: ο άλλος απαντά */}
          {agreement.status === 'cancel_requested' && !iRequestedCancel && multiParties.length === 0 && (
            <ActionBox title={t('agreementDetail.cancelRequestConfirmQuestion', { article: requesterArticle, name: requesterName })} onLayout={markPendingActionLayout}>
              <Button title={t('agreementDetail.cancelAgreeConfirmLabel')} accentColor="#F97316" onPress={() => setPendingAction('cancel_agree')} />
              <Button
                title={t('agreementDetail.doNotWantCancel')}
                variant="secondary"
                accentColor="#F97316"
                onPress={() => setPendingAction('reject_cancel')}
              />
            </ActionBox>
          )}

          {/* Dispute actions (μόνο 2-ατόμων συμφωνίες) */}
          {multiParties.length === 0 && dispute?.status === 'open' && !dispute.i_am_initiator && !dispute.i_have_submitted && (
            <Animated.View
              style={{
                transform: [{ scale: respondPulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.04] }) }],
                borderRadius: radius.md,
                shadowColor: '#EF4444',
                shadowOffset: { width: 0, height: 0 },
                shadowRadius: 14,
                shadowOpacity: respondPulse.interpolate({ inputRange: [0, 1], outputRange: [0.25, 0.9] }),
                elevation: 6,
              }}
            >
              <Button
                title={t('agreementDetail.respondToDispute')}
                icon="alert-circle-outline"
                danger
                onPress={() => setDisputeFlow('respond')}
              />
            </Animated.View>
          )}
          {multiParties.length === 0 && dispute?.status === 'open' && dispute.i_am_initiator && dispute.both_submitted && (
            <Button
              title={t('agreementDetail.manageDispute')}
              icon="eye-outline"
              danger
              onPress={() => setDisputeFlow('resolve')}
            />
          )}
          {multiParties.length === 0 && dispute?.status === 'mutual_resolution_proposed' && dispute.mutual_resolution_proposed_by_me && (
            <View style={styles.notice}>
              <Text style={styles.noticeText}>
                {t('agreementDetail.awaitingMutualResolutionResponse', {
                  article: otherArticle,
                  name: otherPartyName,
                  date: dispute.mutual_resolution_expires_at
                    ? new Date(dispute.mutual_resolution_expires_at).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                    : '—',
                })}
              </Text>
            </View>
          )}
          {multiParties.length === 0 && dispute?.status === 'mutual_resolution_proposed' && !dispute.mutual_resolution_proposed_by_me && (
            <Button
              title={t('agreementDetail.respondToResolutionProposal')}
              icon="people-outline"
              onPress={() => setDisputeFlow('respond')}
            />
          )}

          {(REVIEWABLE_STATUSES as readonly string[]).includes(agreement.status) && (
            <Text style={styles.waiting}>
              {t('agreementDetail.agreementIsStatus', { status: statusLabel(agreement.status) })}
            </Text>
          )}

          {/* Αξιολόγηση μετά από ολοκλήρωση/αθέτηση */}
          {canReview && myReview && (
            <View style={styles.reviewDone}>
              <Text style={styles.reviewDoneTitle}>{t('review.myReview')}</Text>
              <StarRating value={myReview.rating} size={20} />
            </View>
          )}
          {canReview && !myReview && otherPartyId && (
            <ActionBox
              title={
                disputeConcluded
                  ? t('agreementDetail.disputeEndedLeaveReview', { name: otherPartyName })
                  : t('agreementDetail.collaborationEndedLeaveReview', { name: otherPartyName })
              }
              onLayout={markPendingActionLayout}
            >
              <Button
                title={t('agreementDetail.reviewName', { name: otherPartyName })}
                icon="star-outline"
                onPress={() =>
                  router.push({
                    pathname: '/(app)/review',
                    params: {
                      agreementId: agreement.id,
                      revieweeId: otherPartyId,
                      revieweeName: otherPartyName,
                      allowPhoto: agreement.from_offer ? '1' : '',
                    },
                  })
                }
              />
            </ActionBox>
          )}

          {/* Αξιολόγηση πολλών ατόμων — ουρά, ένας κάθε φορά */}
          {canReviewMulti && myMultiReviews.length > 0 && pendingReviewees.length === 0 && (
            <View style={styles.reviewDone}>
              <Text style={styles.reviewDoneTitle}>{t('agreementDetail.allPartiesReviewed')}</Text>
            </View>
          )}
          {canReviewMulti && pendingReviewees.length > 0 && (
            <ActionBox
              title={
                multiDisputeConcluded
                  ? t('agreementDetail.disputeEndedLeaveReviews', { count: pendingReviewees.length })
                  : t('agreementDetail.collaborationEndedLeaveReviews', { count: pendingReviewees.length })
              }
              onLayout={markPendingActionLayout}
            >
              <Button
                title={t('agreementDetail.reviewName', { name: pendingReviewees[0].name })}
                icon="star-outline"
                onPress={() =>
                  router.push({
                    pathname: '/(app)/review',
                    params: {
                      agreementId: agreement.id,
                      revieweeId: pendingReviewees[0].id,
                      revieweeName: pendingReviewees[0].name,
                      queueIds: pendingReviewees.map((p) => p.id).join('|'),
                      queueNames: pendingReviewees.map((p) => p.name).join('|'),
                      allowPhoto: agreement.from_offer ? '1' : '',
                    },
                  })
                }
              />
            </ActionBox>
          )}

          {needsMyDecision ? null : agreement.creator_agreed && agreement.counterparty_agreed ? (
            <View>
              <Button
                title={t('agreements.action_exportPdf')}
                icon="print-outline"
                onPress={onExportPdf}
                loading={exporting}
              />
              {pdfOutdated && !exporting && (
                <View style={styles.pdfOutdatedBadge}>
                  <Text style={styles.pdfOutdatedBadgeText}>{t('agreementDetail.pdfOutdated')}</Text>
                </View>
              )}
            </View>
          ) : agreement.status !== 'draft' ? (
            <View>
              <Button
                title={t('agreements.action_exportPdf')}
                variant="secondary"
                icon="print-outline"
                disabled
                onPress={() => {}}
              />
              <Text style={styles.pdfNote}>{t('agreements.pdfAvailableAfterSigning')}</Text>
            </View>
          ) : null}
        </ScrollView>
      </View>

      <SuccessOverlay
        visible={!!success}
        message={success ?? undefined}
        onDone={() => setSuccess(null)}
      />
      <AgreementSealAnimation visible={showSeal} onDone={() => setShowSeal(false)} />
      <MoneyHandoffAnimation visible={showMoneyHandoff} onDone={() => { setShowMoneyHandoff(false); void load(); }} />
      <HandshakeAnimation visible={showHandshake} onDone={() => setShowHandshake(false)} />
      <AgreementTearAnimation visible={showTear} onDone={() => setShowTear(false)} />

      {/* Dispute flows */}
      {disputeFlow === 'open' && (
        <OpenDisputeFlow
          agreementId={agreement.id}
          onDone={() => { setDisputeFlow(null); void load(); }}
          onCancel={() => setDisputeFlow(null)}
        />
      )}
      {disputeFlow === 'respond' && dispute && dispute.status === 'mutual_resolution_proposed' && !dispute.mutual_resolution_proposed_by_me && (
        <RespondMutualResolutionFlow
          dispute={dispute}
          initiatorName={otherPartyName}
          onDone={() => { setDisputeFlow(null); void load(); }}
          onCancel={() => setDisputeFlow(null)}
        />
      )}
      {disputeFlow === 'respond' && dispute && dispute.status !== 'mutual_resolution_proposed' && (
        <RespondDisputeFlow
          dispute={dispute}
          onDone={() => { setDisputeFlow(null); void load(); }}
          onCancel={() => setDisputeFlow(null)}
        />
      )}
      {disputeFlow === 'resolve' && dispute && (
        <ResolveDisputeFlow
          dispute={dispute}
          otherName={otherPartyName}
          onDone={() => { setDisputeFlow(null); void load(); }}
          onCancel={() => setDisputeFlow(null)}
        />
      )}

      {activeConfig && (
        <ConfirmModal
          visible
          title={activeConfig.title}
          message={activeConfig.message}
          bullets={activeConfig.bullets}
          warning={activeConfig.warning}
          confirmLabel={activeConfig.confirmLabel}
          danger={activeConfig.danger}
          tags={activeConfig.tags}
          loading={busy}
          error={modalError}
          onConfirm={runConfirm}
          onCancel={closeConfirm}
        />
      )}

      {!!earlyCompletionExpiry && (
        <ConfirmModal
          visible
          title={t('agreementDetail.earlyCompletionTitle')}
          message={t('agreementDetail.earlyCompletionBody', { date: earlyCompletionExpiry })}
          confirmLabel={t('agreementDetail.yesCompleteEarly')}
          onConfirm={() => { setEarlyCompletionExpiry(null); void sendCompletionRequest(); }}
          onCancel={() => setEarlyCompletionExpiry(null)}
        />
      )}

      <ConfirmModal
        visible={showWithdrawConfirm}
        title={t('agreementDetail.multiWithdrawTitle')}
        message={t('agreementDetail.multiWithdrawMessage')}
        confirmLabel={t('agreementDetail.multiWithdrawConfirm')}
        danger
        onConfirm={doWithdrawMultiParty}
        onCancel={() => setShowWithdrawConfirm(false)}
      />

      <ConfirmModal
        visible={showInsufficientInk}
        title={t('agreementDetail.insufficientInkTitle')}
        message={t('agreementDetail.insufficientInkBody')}
        confirmLabel={t('wallet.topUp')}
        onConfirm={() => { setShowInsufficientInk(false); router.push('/(app)/wallet/topup'); }}
        onCancel={() => setShowInsufficientInk(false)}
      />

      <Modal visible={showEditRequestModal} transparent animationType="fade" onRequestClose={() => setShowEditRequestModal(false)}>
        <KeyboardAvoidingView style={styles.editModalOverlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.editModalCard}>
            <Text style={styles.editModalTitle}>{t('agreementDetail.editRequestModalTitle')}</Text>
            <Text style={styles.editModalSubtitle}>{t('agreementDetail.editRequestModalSubtitle')}</Text>
            <TextInput
              style={styles.editModalInput}
              value={editRequestNote}
              onChangeText={setEditRequestNote}
              placeholder={t('agreementDetail.editRequestPlaceholder')}
              placeholderTextColor={colors.textMuted}
              multiline
              numberOfLines={4}
              maxLength={500}
            />
            <View style={styles.actionRow}>
              <View style={styles.actionBtn}>
                <Button title={t('common.cancel')} variant="secondary" onPress={() => setShowEditRequestModal(false)} />
              </View>
              <View style={styles.actionBtn}>
                <Button title={t('common.send')} onPress={onSubmitEditRequest} loading={editBusy} />
              </View>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      <Modal visible={showAccuseModal} transparent animationType="fade" onRequestClose={() => setShowAccuseModal(false)}>
        <KeyboardAvoidingView style={styles.editModalOverlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <ScrollView contentContainerStyle={styles.editModalCard}>
            {accuseStep === 1 && (
              <>
                <Text style={styles.editModalTitle}>{t('agreementDetail.whoIsDisputeAbout')}</Text>
                {multiParties.filter((mp) => mp.user_id && mp.user_id !== user?.id && !mp.withdrawn_at).map((mp) => (
                  <Pressable
                    key={mp.id}
                    style={[styles.partyRow, accuseTarget === mp.user_id && { opacity: 0.6 }]}
                    onPress={() => setAccuseTarget(mp.user_id)}
                  >
                    <Ionicons name={accuseTarget === mp.user_id ? 'radio-button-on' : 'radio-button-off'} size={18} color={colors.primary} />
                    <Text style={styles.partyName}>{profiles[mp.user_id!]?.full_name || t('common.user')}</Text>
                  </Pressable>
                ))}
                <Text style={[styles.editModalTitle, { fontSize: fontSize.md, marginTop: spacing.sm }]}>{t('dispute.open_step1_title')}</Text>
                <View style={styles.tagGrid}>
                  {DISPUTE_TAGS.map((tg) => {
                    const sel = accuseTags.includes(tg.key);
                    return (
                      <Pressable
                        key={tg.key}
                        style={[styles.tag, sel && styles.tagSelected]}
                        onPress={() => setAccuseTags((prev) => (prev.includes(tg.key) ? prev.filter((k) => k !== tg.key) : [...prev, tg.key]))}
                      >
                        <Text style={[styles.tagText, sel && styles.tagTextSelected]}>{tg.label}</Text>
                      </Pressable>
                    );
                  })}
                </View>
                <View style={styles.actionRow}>
                  <View style={styles.actionBtn}>
                    <Button title={t('common.cancel')} variant="secondary" onPress={() => { setShowAccuseModal(false); setAccuseStep(1); setAccuseTarget(null); setAccuseTags([]); setAccuseNote(''); setAccuseEvidence([]); }} />
                  </View>
                  <View style={styles.actionBtn}>
                    <Button title={t('common.next')} onPress={() => setAccuseStep(2)} disabled={!accuseTarget || accuseTags.length === 0} />
                  </View>
                </View>
              </>
            )}
            {accuseStep === 2 && (
              <>
                <Text style={styles.editModalTitle}>{t('dispute.open_step2_title')}</Text>
                <Text style={styles.statementText}>{t('dispute.open_step2_body')}</Text>
                <TextInput
                  value={accuseNote}
                  onChangeText={setAccuseNote}
                  placeholder={t('dispute.open_step2_placeholder')}
                  placeholderTextColor={colors.textMuted}
                  multiline
                  numberOfLines={4}
                  style={[styles.noteInput, { color: colors.text, backgroundColor: colors.surface, borderColor: colors.border }]}
                />
                <View style={styles.actionRow}>
                  <View style={styles.actionBtn}>
                    <Button title={t('common.back')} variant="secondary" onPress={() => setAccuseStep(1)} />
                  </View>
                  <View style={styles.actionBtn}>
                    <Button title={t('common.next')} onPress={() => setAccuseStep(3)} />
                  </View>
                </View>
              </>
            )}
            {accuseStep === 3 && (
              <>
                <Text style={styles.editModalTitle}>{t('dispute.evidence_title')}</Text>
                <EvidenceAttachSection agreementId={agreement?.id ?? ''} evidence={accuseEvidence} onChange={setAccuseEvidence} />
                <View style={styles.actionRow}>
                  <View style={styles.actionBtn}>
                    <Button title={t('common.back')} variant="secondary" onPress={() => setAccuseStep(2)} />
                  </View>
                  <View style={styles.actionBtn}>
                    <Button title={t('agreementDetail.openDispute')} onPress={onOpenMultiPartyDispute} loading={multiBusy} disabled={!accuseTarget || accuseTags.length === 0} />
                  </View>
                </View>
              </>
            )}
          </ScrollView>
        </KeyboardAvoidingView>
      </Modal>

      <Modal visible={showDisagreeModal} transparent animationType="fade" onRequestClose={() => setShowDisagreeModal(false)}>
        <KeyboardAvoidingView style={styles.editModalOverlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <ScrollView contentContainerStyle={styles.editModalCard}>
            {disagreeStep === 1 && (
              <>
                <Text style={styles.editModalTitle}>{t('dispute.open_step1_title')}</Text>
                <View style={styles.tagGrid}>
                  {DISPUTE_TAGS.map((tg) => {
                    const sel = disagreeTags.includes(tg.key);
                    return (
                      <Pressable
                        key={tg.key}
                        style={[styles.tag, sel && styles.tagSelected]}
                        onPress={() => setDisagreeTags((prev) => (prev.includes(tg.key) ? prev.filter((k) => k !== tg.key) : [...prev, tg.key]))}
                      >
                        <Text style={[styles.tagText, sel && styles.tagTextSelected]}>{tg.label}</Text>
                      </Pressable>
                    );
                  })}
                </View>
                <View style={styles.actionRow}>
                  <View style={styles.actionBtn}>
                    <Button title={t('common.cancel')} variant="secondary" onPress={() => { setShowDisagreeModal(false); setDisagreeStep(1); setDisagreeTags([]); setDisagreeNote(''); setDisagreeEvidence([]); }} />
                  </View>
                  <View style={styles.actionBtn}>
                    <Button title={t('common.next')} accentColor={colors.danger} onPress={() => setDisagreeStep(2)} disabled={disagreeTags.length === 0} />
                  </View>
                </View>
              </>
            )}
            {disagreeStep === 2 && (
              <>
                <Text style={styles.editModalTitle}>{t('dispute.open_step2_title')}</Text>
                <Text style={styles.statementText}>{t('dispute.open_step2_body')}</Text>
                <TextInput
                  value={disagreeNote}
                  onChangeText={setDisagreeNote}
                  placeholder={t('dispute.open_step2_placeholder')}
                  placeholderTextColor={colors.textMuted}
                  multiline
                  numberOfLines={4}
                  style={[styles.noteInput, { color: colors.text, backgroundColor: colors.surface, borderColor: colors.border }]}
                />
                <View style={styles.actionRow}>
                  <View style={styles.actionBtn}>
                    <Button title={t('common.back')} variant="secondary" onPress={() => setDisagreeStep(1)} />
                  </View>
                  <View style={styles.actionBtn}>
                    <Button title={t('common.next')} accentColor={colors.danger} onPress={() => setDisagreeStep(3)} />
                  </View>
                </View>
              </>
            )}
            {disagreeStep === 3 && (
              <>
                <Text style={styles.editModalTitle}>{t('dispute.evidence_title')}</Text>
                <EvidenceAttachSection agreementId={agreement?.id ?? ''} evidence={disagreeEvidence} onChange={setDisagreeEvidence} />
                <View style={styles.actionRow}>
                  <View style={styles.actionBtn}>
                    <Button title={t('common.back')} variant="secondary" onPress={() => setDisagreeStep(2)} />
                  </View>
                  <View style={styles.actionBtn}>
                    <Button
                      title={t('agreementDetail.disagree')}
                      danger
                      onPress={() => onRespondMultiParty(false, disagreeTags, disagreeNote)}
                      loading={multiBusy}
                      disabled={disagreeTags.length === 0}
                    />
                  </View>
                </View>
              </>
            )}
          </ScrollView>
        </KeyboardAvoidingView>
      </Modal>

      <Modal visible={showEditForm} transparent animationType="fade" onRequestClose={() => setShowEditForm(false)}>
        <KeyboardAvoidingView style={styles.editModalOverlay} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <ScrollView contentContainerStyle={styles.editModalCard}>
            <Text style={styles.editModalTitle}>{t('agreementDetail.editAgreementTitle')}</Text>
            <Text style={styles.editModalSubtitle}>{t('agreements.fields.title')}</Text>
            <TextInput style={styles.editModalInputSingle} value={editTitle} onChangeText={setEditTitle} placeholderTextColor={colors.textMuted} />
            <Text style={styles.editModalSubtitle}>{t('agreementDetail.termsDescriptionLabel')}</Text>
            <TextInput style={styles.editModalInput} value={editDescription} onChangeText={setEditDescription} multiline numberOfLines={4} placeholderTextColor={colors.textMuted} />
            <Text style={styles.editModalSubtitle}>{t('agreementDetail.labelAmountPlain')}</Text>
            <TextInput style={styles.editModalInputSingle} value={editAmount} onChangeText={(v) => setEditAmount(formatGroupedAmount(v))} keyboardType="decimal-pad" placeholderTextColor={colors.textMuted} />
            <View style={styles.actionRow}>
              <View style={styles.actionBtn}>
                <Button title={t('common.cancel')} variant="secondary" onPress={() => setShowEditForm(false)} />
              </View>
              <View style={styles.actionBtn}>
                <Button title={t('common.save')} onPress={onSaveEditForm} loading={editBusy} />
              </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
}

function ActionBox({ title, children, onLayout }: { title: string; children: React.ReactNode; onLayout?: (e: LayoutChangeEvent) => void }) {
  const scale = useRef(new Animated.Value(0.88)).current;

  useEffect(() => {
    const t = setTimeout(() => {
      Animated.spring(scale, {
        toValue: 1,
        useNativeDriver: Platform.OS !== 'web',
        bounciness: 16,
        speed: 9,
      }).start();
    }, 1000);
    return () => clearTimeout(t);
  }, [scale]);

  return (
    <Animated.View style={[styles.actionBox, { transform: [{ scale }] }]} onLayout={onLayout}>
      <Text style={styles.actionBoxTitle}>{title}</Text>
      {children}
    </Animated.View>
  );
}

function SignatureRow({
  name,
  signature,
  signatureSvg,
  verified,
  agreedAt,
  code,
}: {
  name: string;
  signature?: string | null;
  signatureSvg?: string | null;
  verified: boolean;
  agreedAt: string | null;
  code: string;
}) {
  const { t } = useTranslation();
  return (
    <View style={styles.sigRow}>
      {parseSignature(signatureSvg) ? (
        <SignatureView data={signatureSvg} width={190} color={colors.primary} />
      ) : (
        <Text style={styles.sigName}>{signature?.trim() || name}</Text>
      )}
      <View style={styles.sigHeader}>
        <Text style={styles.sigSubName}>{name}</Text>
        {verified && <VerifiedBadge compact />}
      </View>
      <Text style={styles.sigMeta}>
        {t('agreements.signedDigitally')}{agreedAt ? ` · ${formatDateTime(agreedAt)}` : ''}
      </Text>
      <Text style={styles.sigCode}>{t('agreements.signatureCode')} {code}</Text>
    </View>
  );
}

function Row({
  label,
  value,
  onPress,
  verified,
  levelInfo,
}: {
  label: string;
  value: string;
  onPress?: () => void;
  verified?: boolean;
  levelInfo?: LevelInfo;
}) {
  const badges = (
    <>
      {verified && <VerifiedBadge compact />}
      {levelInfo && <LevelBadge info={levelInfo} size="sm" />}
    </>
  );
  if (onPress) {
    return (
      <Pressable style={styles.row} onPress={onPress} accessibilityRole="button">
        <Text style={styles.rowLabel}>{label}</Text>
        <View style={styles.rowValueRow}>
          {badges}
          <Text style={[styles.rowValue, styles.rowValueLink]}>{value} ›</Text>
        </View>
      </Pressable>
    );
  }
  return (
    <View style={styles.row}>
      <Text style={styles.rowLabel}>{label}</Text>
      <View style={styles.rowValueRow}>
        {badges}
        <Text style={styles.rowValue}>{value}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1, backgroundColor: colors.background },
  editModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    padding: spacing.lg,
  },
  editModalCard: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: spacing.lg,
    gap: spacing.sm,
  },
  editModalTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
  statementText: { color: colors.textMuted, fontSize: fontSize.sm, marginTop: 2 },
  tagGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  tag: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radius.md,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  tagSelected: { backgroundColor: colors.primary, borderColor: colors.primary },
  tagText: { color: colors.text, fontSize: fontSize.sm },
  tagTextSelected: { color: '#fff' },
  noteInput: {
    borderWidth: 1,
    borderRadius: radius.md,
    padding: spacing.sm,
    minHeight: 90,
    textAlignVertical: 'top',
    fontSize: fontSize.sm,
  },
  partyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  partyName: { flex: 1, color: colors.text, fontSize: fontSize.md, fontWeight: '600' },
  partyNameWithdrawn: { color: colors.textMuted, textDecorationLine: 'line-through' },
  partyShare: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '700' },
  partyWithdrawnTag: { color: colors.danger, fontSize: fontSize.xs, fontWeight: '700' },
  editModalSubtitle: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600', marginTop: spacing.xs },
  editModalInput: {
    backgroundColor: colors.surfaceAlt,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.sm,
    color: colors.text,
    fontSize: fontSize.md,
    minHeight: 90,
    textAlignVertical: 'top',
  },
  editModalInputSingle: {
    backgroundColor: colors.surfaceAlt,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.sm,
    color: colors.text,
    fontSize: fontSize.md,
    height: 44,
  },
  loader: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
    backgroundColor: colors.background,
  },
  notFound: {
    color: colors.text,
    fontSize: fontSize.md,
  },
  content: {
    paddingHorizontal: spacing.lg,
    gap: spacing.lg,
  },
  topBar: { flexDirection: 'row' },
  back: {
    color: colors.primaryLight,
    fontSize: fontSize.md,
    fontWeight: '600',
  },
  docHeader: {
    gap: spacing.xs,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  docId: {
    color: colors.textMuted,
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 1.5,
    fontFamily: 'monospace',
  },
  headerMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.xs,
  },
  docDate: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
  },
  title: {
    color: colors.text,
    fontSize: 22,
    fontWeight: '800',
    lineHeight: 30,
    letterSpacing: -0.3,
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    gap: spacing.sm,
    ...shadow.card,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.md,
  },
  rowLabel: {
    color: colors.textMuted,
    fontSize: fontSize.md,
  },
  rowValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexShrink: 1,
    justifyContent: 'flex-end',
  },
  rowValue: {
    color: colors.text,
    fontSize: fontSize.md,
    fontWeight: '600',
    flexShrink: 1,
    textAlign: 'right',
  },
  rowValueLink: {
    color: colors.primaryLight,
  },
  reviewDone: {
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
  },
  reviewDoneTitle: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
    fontWeight: '600',
  },
  sectionTitle: {
    color: colors.textMuted,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  dividerLine: {
    height: 1,
    backgroundColor: colors.border,
    marginVertical: spacing.xs,
  },
  signatureCard: {
    backgroundColor: colors.surface,
  },
  commitment: {
    color: colors.text,
    fontSize: fontSize.md,
    fontWeight: '600',
    lineHeight: 24,
    fontStyle: 'italic',
  },
  sigRow: {
    gap: 4,
    paddingVertical: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  sigHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  sigName: {
    color: colors.primary,
    fontSize: fontSize.xl,
    fontWeight: '700',
    fontStyle: 'italic',
  },
  sigSubName: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
    fontWeight: '600',
  },
  sigMeta: {
    color: colors.success,
    fontSize: fontSize.sm,
    fontWeight: '600',
  },
  sigCode: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
    letterSpacing: 1,
  },
  signedSeal: {
    color: colors.success,
    fontSize: fontSize.sm,
    fontWeight: '700',
    textAlign: 'center',
    borderWidth: 1.5,
    borderColor: colors.success,
    borderRadius: radius.md,
    borderStyle: 'dashed',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginTop: spacing.sm,
    letterSpacing: 0.5,
  },
  description: {
    color: colors.text,
    fontSize: fontSize.md,
    lineHeight: 22,
  },
  paymentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.xs,
  },
  paymentLeft: {
    gap: 2,
  },
  paymentAmount: {
    color: colors.text,
    fontSize: fontSize.md,
    fontWeight: '600',
  },
  paymentDate: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
  },
  paymentStatus: {
    fontSize: fontSize.sm,
    fontWeight: '700',
  },
  notice: {
    backgroundColor: colors.surfaceAlt,
    borderRadius: radius.md,
    padding: spacing.md,
  },
  noticeWaiting: {
    backgroundColor: 'rgba(251,191,36,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(251,191,36,0.30)',
  },
  noticeText: {
    color: colors.text,
    fontSize: fontSize.sm,
    lineHeight: 20,
  },
  actions: {
    gap: spacing.md,
  },
  pdfOutdatedBadge: {
    position: 'absolute',
    top: -8,
    right: -6,
    backgroundColor: colors.danger,
    borderRadius: radius.full,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    ...shadow.card,
  },
  pdfOutdatedBadgeText: {
    color: '#fff',
    fontSize: fontSize.xs,
    fontWeight: '700',
  },
  updateBubble: {
    position: 'absolute',
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.primary,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: radius.full,
    ...shadow.card,
    zIndex: 20,
  },
  updateBubbleText: {
    color: '#fff',
    fontSize: fontSize.sm,
    fontWeight: '700',
  },
  footerBar: {
    maxHeight: '55%',
    backgroundColor: colors.background,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
  },
  acceptBtn: {
    height: 52,
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    backgroundColor: colors.primary,
  },
  acceptBtnOrange: {
    backgroundColor: '#F97316',
  },
  acceptPressed: { opacity: 0.85 },
  acceptDisabled: { opacity: 0.5 },
  acceptLabel: {
    color: colors.textOnPrimary,
    fontSize: fontSize.md,
    fontWeight: '600',
  },
  acceptStrike: {
    color: colors.textOnPrimary,
    textDecorationLine: 'line-through',
    opacity: 0.8,
  },
  acceptFree: {
    color: colors.success,
    fontWeight: '800',
  },
  actionRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  actionBtn: {
    flex: 1,
  },
  waiting: {
    color: colors.textMuted,
    fontSize: fontSize.md,
    textAlign: 'center',
  },
  editRequestLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    alignSelf: 'flex-start',
    paddingVertical: 4,
    paddingHorizontal: 2,
  },
  editRequestLinkText: {
    color: colors.textMuted,
    fontSize: fontSize.xs,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  pdfNote: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  error: {
    color: colors.danger,
    fontSize: fontSize.sm,
    textAlign: 'center',
  },
  reasonWho: {
    color: colors.textMuted,
    fontWeight: '700',
  },
  cancelTagsBlock: {
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  cancelTagsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  cancelTag: {
    borderRadius: radius.full,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: 4,
    paddingHorizontal: spacing.sm,
    backgroundColor: colors.surfaceAlt,
  },
  cancelTagText: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalCard: {
    backgroundColor: colors.surface,
    borderTopLeftRadius: radius.lg,
    borderTopRightRadius: radius.lg,
    padding: spacing.lg,
    gap: spacing.md,
  },
  modalTitle: {
    color: colors.text,
    fontSize: fontSize.lg,
    fontWeight: '700',
  },
  modalHint: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
  },
  modalInput: {
    minHeight: 90,
    backgroundColor: colors.surfaceAlt,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    color: colors.text,
    fontSize: fontSize.md,
    textAlignVertical: 'top',
  },
  actionBox: {
    backgroundColor: 'rgba(249,115,22,0.18)',
    borderRadius: radius.lg,
    borderWidth: 1.5,
    borderColor: 'rgba(249,115,22,0.7)',
    padding: spacing.md,
    gap: spacing.sm,
  },
  actionBoxTitle: {
    color: '#FFFFFF',
    fontSize: fontSize.sm,
    fontWeight: '700',
    lineHeight: 20,
  },
});
